import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  fetchTeachingUnits,
  fetchTeachingUnitDetail,
  fetchTeachingMeta,
  fetchTeachingQuickstartContext,
  fetchImageSearch,
  fetchKeywordDetails,
  presentQuickstartDisplayText,
  createTeachingUnitApi,
  updateTeachingUnitApi,
  addQuickstartCompetencyBundle,
  removeQuickstartCompetencyBundle,
  fetchTeachingQuickstartState,
  fetchTeachingQuickstartStudentCompetencySummary,
  saveTeachingQuickstartState,
  connectQuickstartDisplay,
  disconnectQuickstartDisplay,
  fetchQuickstartDisplayConnection,
  fetchQuickstartDisplayStatus,
  presentQuickstartDisplayImage,
  presentQuickstartDisplayMedia,
  uploadTeachingMediaFile,
  moveTeachingUnitToTrash,
  restoreTeachingUnitFromTrash,
  TeachingUnitSummary,
  TeachingUnitDetail,
  TeachingUnitCreatePayload,
  TeachingPreparationMeta,
  TeachingQuickstartContext
} from '../lib/unterrichtenApi';
import { apiClient } from '../lib/api';
import {
  saveDisplaySnapshot,
  listDisplaySnapshots,
  restoreDisplaySnapshot,
  deleteDisplaySnapshot,
  updateDisplaySnapshot,
  fetchYouTubeTitle,
  teacherDisableLernbueroDashboard,
  type DisplaySnapshotSummary
} from '../lib/displayApi';
import { format, parseISO } from 'date-fns';
import {
  Calendar,
  CheckCircle2,
  Loader2,
  PlusCircle,
  Users,
  Target,
  Info,
  RefreshCw,
  Mail,
  Minus,
  Play,
  Pause,
  Square,
  Trash2,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Monitor,
  Unplug,
  Lightbulb,
  Image,
  X,
  Save,
  FolderOpen,
  Star,
  BookOpen
} from 'lucide-react';
import EmailModal from '../components/EmailModal';
import DisplayMirrorPanel from '../components/DisplayMirrorPanel';
import { getPhotoUrl } from '../lib/photoUtils';
import AbsenceToggle from '../components/AbsenceToggle';
import { fetchActiveAbsences } from '../lib/temporaryAbsencesApi';
import StudentSeatCard from '../components/liveLesson/StudentSeatCard';
import {
  useFitSquareGridSize,
  useViewportRemainingHeight,
  type FitSquareGridSpec
} from '../hooks/useFitSquareGridSize';

type ParentType = 'class' | 'course' | 'group' | 'ad_hoc';


interface ClassAggregate {
  Klasse: string;
  student_count: number;
}

interface ContextStudent {
  id: string;
  firstName: string;
  lastName: string;
  className?: string | null;
}

interface QuickstartStudentView {
  id: string;
  firstName: string;
  lastName: string;
  className: string;
}

interface QuickstartPlannedMediaItem {
  id: string;
  type: 'media' | 'text';
  assetId?: number | null;
  title: string;
  mediaUrl?: string;
  mediaType?: 'image' | 'video' | 'pdf' | 'youtube';
  mimeType?: string;
  imageUrl?: string;
  sourcePageUrl?: string;
  originalName?: string;
  uploadedAt?: string | null;
  lastUsedAt?: string | null;
  usageCount?: number;
  text?: string;
  createdAt?: string;
}

const statusOptions: TeachingUnitSummary['status'][] = ['draft', 'active', 'paused', 'completed', 'archived'];
const UNTERRICHTEN_SESSION_KEY = 'unterrichten:session:v1';
const QUICKSTART_STUDENT_TILE_GAP = 8;
const QUICKSTART_STUDENT_GRID_PADDING = 12;

function createQuickstartMediaId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `media_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function inferPlannedMediaType({ mimeType, url }: { mimeType?: string; url?: string }): 'image' | 'video' | 'pdf' | 'youtube' {
  const normalizedMime = String(mimeType || '').toLowerCase();
  if (normalizedMime.startsWith('video/')) return 'video';
  if (normalizedMime === 'application/pdf') return 'pdf';
  const lowerUrl = String(url || '').toLowerCase();
  if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) return 'youtube';
  if (lowerUrl.endsWith('.pdf')) return 'pdf';
  if (/\.(mp4|webm|ogg|mov|m4v)(\?|#|$)/i.test(lowerUrl)) return 'video';
  return 'image';
}

function toYouTubeEmbedUrl(rawUrl: string): string {
  const value = String(rawUrl || '').trim();
  if (!value) return '';
  try {
    const parsed = new URL(value);
    const host = parsed.hostname.toLowerCase();
    if (host.includes('youtu.be')) {
      const id = parsed.pathname.replace('/', '').trim();
      return id ? `https://www.youtube-nocookie.com/embed/${id}` : value;
    }
    if (host.includes('youtube.com')) {
      if (parsed.pathname.startsWith('/embed/')) {
        return value;
      }
      if (parsed.pathname.startsWith('/shorts/')) {
        const id = parsed.pathname.replace('/shorts/', '').split('/')[0];
        return id ? `https://www.youtube-nocookie.com/embed/${id}` : value;
      }
      if (parsed.pathname.startsWith('/live/')) {
        const id = parsed.pathname.replace('/live/', '').split('/')[0];
        return id ? `https://www.youtube-nocookie.com/embed/${id}` : value;
      }
      const id = parsed.searchParams.get('v');
      if (id) {
        return `https://www.youtube-nocookie.com/embed/${id}`;
      }
    }
  } catch (error) {
    return value;
  }
  return value;
}

export default function Unterrichten() {
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<TeachingUnitSummary['status'] | 'all'>('all');
  const [listScope, setListScope] = useState<'normal' | 'archive' | 'trash'>('normal');
  const [parentType, setParentType] = useState<ParentType>('class');
  const [selectedParentId, setSelectedParentId] = useState<string | number | ''>('');
  const [selectedClassName, setSelectedClassName] = useState<string>('');
  const [selectedUnitId, setSelectedUnitId] = useState<number | null>(null);

  const [title, setTitle] = useState('');
  const [subjectId, setSubjectId] = useState<number | ''>('');
  const [curriculumId, setCurriculumId] = useState<number | ''>('');
  const [primarySitzplanId, setPrimarySitzplanId] = useState<number | ''>('');
  const [nfcAutoEnabled, setNfcAutoEnabled] = useState(false);
  const [docAutoEnabled, setDocAutoEnabled] = useState(false);
  const [startAt, setStartAt] = useState<string>('');
  const [endAt, setEndAt] = useState<string>('');
  const [contextStudents, setContextStudents] = useState<ContextStudent[]>([]);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextError, setContextError] = useState<string | null>(null);
  const [unterrichtenMode, setUnterrichtenMode] = useState<'ad_hoc' | 'structure'>('ad_hoc');
  const [quickstartContextType, setQuickstartContextType] = useState<'class' | 'course' | 'group'>('class');
  const [quickstartClassName, setQuickstartClassName] = useState('');
  const [quickstartCourseId, setQuickstartCourseId] = useState<number | ''>('');
  const [quickstartGroupId, setQuickstartGroupId] = useState<number | ''>('');
  const [quickstartSubjectId, setQuickstartSubjectId] = useState<number | ''>('');
  const [quickstartTheme, setQuickstartTheme] = useState('');
  const [quickstartContext, setQuickstartContext] = useState<TeachingQuickstartContext | null>(null);
  const [quickstartLoading, setQuickstartLoading] = useState(false);
  const [quickstartError, setQuickstartError] = useState<string | null>(null);
  const [selectedQuickstartSitzplanId, setSelectedQuickstartSitzplanId] = useState<number | null>(null);
  const [selectedCompetencyIds, setSelectedCompetencyIds] = useState<number[]>([]);
  const [quickstartTeachingUnitId, setQuickstartTeachingUnitId] = useState<number | null>(null);
  const [quickstartTeachingStatus, setQuickstartTeachingStatus] = useState<TeachingUnitSummary['status'] | null>(null);
  const [quickstartActionLoadingStudentId, setQuickstartActionLoadingStudentId] = useState<string | null>(null);
  const [selectedStudentForEmail, setSelectedStudentForEmail] = useState<QuickstartStudentView | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedKeywordHint, setSelectedKeywordHint] = useState<{ keyword: string; explanation: string } | null>(
    null
  );
  const [imageSearchInput, setImageSearchInput] = useState('');
  const [imageSearchLoading, setImageSearchLoading] = useState(false);
  const [manualImageGallery, setManualImageGallery] = useState<import('../lib/unterrichtenApi').QuickstartImageGalleryGroup[]>([]);
  const [plannedMediaTitleInput, setPlannedMediaTitleInput] = useState('');
  const [plannedMediaUrlInput, setPlannedMediaUrlInput] = useState('');
  const [plannedMediaTextInput, setPlannedMediaTextInput] = useState('');
  const [plannedMediaUploading, setPlannedMediaUploading] = useState(false);
  const [plannedMediaItems, setPlannedMediaItems] = useState<QuickstartPlannedMediaItem[]>([]);
  const [showKeywordsModal, setShowKeywordsModal] = useState(false);
  const [showImagesModal, setShowImagesModal] = useState(false);
  const [keywordDetailText, setKeywordDetailText] = useState('');
  const [keywordDetailLoading, setKeywordDetailLoading] = useState(false);
  const [keywordDisplaySending, setKeywordDisplaySending] = useState(false);
  const [quickstartBookingCounts, setQuickstartBookingCounts] = useState<Record<string, number>>({});
  const [quickstartAwardedCompetencyCounts, setQuickstartAwardedCompetencyCounts] = useState<Record<string, number>>({});
  const [quickstartNoSitzplanStudents, setQuickstartNoSitzplanStudents] = useState<QuickstartStudentView[] | null>(null);
  const [quickstartDisplayToastEnabled, setQuickstartDisplayToastEnabled] = useState(true);
  const [quickstartDisplayRankingToastEnabled, setQuickstartDisplayRankingToastEnabled] = useState(false);
  const [displayPin, setDisplayPin] = useState('');
  const [displayConnectLoading, setDisplayConnectLoading] = useState(false);
  const [displayDisconnectLoading, setDisplayDisconnectLoading] = useState(false);
  const [connectedDisplayPin, setConnectedDisplayPin] = useState<string | null>(null);
  const [showDisplayConnectForm, setShowDisplayConnectForm] = useState(true);
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
  const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(false);
  const [switchingUnitId, setSwitchingUnitId] = useState<number | null>(null);
  const [displaySnapshots, setDisplaySnapshots] = useState<DisplaySnapshotSummary[]>([]);
  const [showSnapshotPanel, setShowSnapshotPanel] = useState(false);
  const [quickstartCenterView, setQuickstartCenterView] = useState<'students' | 'display'>('students');
  // Beim ersten Verbinden eines Displays Tafelbild-Panel öffnen, damit Speichern/Laden sichtbar ist
  const prevConnectedPinRef = useRef<string | null>(null);
  useEffect(() => {
    if (connectedDisplayPin && connectedDisplayPin !== prevConnectedPinRef.current) {
      prevConnectedPinRef.current = connectedDisplayPin;
      setShowSnapshotPanel(true);
    }
    if (!connectedDisplayPin) prevConnectedPinRef.current = null;
  }, [connectedDisplayPin]);
  const [snapshotSaving, setSnapshotSaving] = useState(false);
  const [snapshotRestoring, setSnapshotRestoring] = useState<number | null>(null);
  const [snapshotNameInput, setSnapshotNameInput] = useState('');
  const [snapshotFolderInput, setSnapshotFolderInput] = useState('');
  const [snapshotSearchInput, setSnapshotSearchInput] = useState('');
  const [snapshotFavoritesOnly, setSnapshotFavoritesOnly] = useState(false);
  const quickstartStateSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stateRestoreDoneRef = useRef(false);
  const statePersistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const plannedMediaFileInputRef = useRef<HTMLInputElement | null>(null);
  const quickstartWorkspaceAnchorRef = useRef<HTMLDivElement | null>(null);
  const quickstartStudentsGridRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(UNTERRICHTEN_SESSION_KEY);
      if (!raw) {
        stateRestoreDoneRef.current = true;
        return;
      }
      const snapshot = JSON.parse(raw) as any;
      const savedAt = Number(snapshot?.savedAt || 0);
      // Ignore very old snapshots to avoid reviving stale workflow context.
      if (savedAt && Date.now() - savedAt > 1000 * 60 * 60 * 24) {
        sessionStorage.removeItem(UNTERRICHTEN_SESSION_KEY);
        stateRestoreDoneRef.current = true;
        return;
      }

      if (snapshot.statusFilter) {
        setStatusFilter(snapshot.statusFilter);
      }
      if (snapshot.listScope) {
        setListScope(snapshot.listScope);
      }
      if (snapshot.unterrichtenMode) {
        setUnterrichtenMode(snapshot.unterrichtenMode);
      }
      if (snapshot.selectedUnitId !== undefined) {
        setSelectedUnitId(snapshot.selectedUnitId);
      }
      if (snapshot.parentType) {
        setParentType(snapshot.parentType);
      }
      if (snapshot.selectedParentId !== undefined) {
        setSelectedParentId(snapshot.selectedParentId);
      }
      if (typeof snapshot.selectedClassName === 'string') {
        setSelectedClassName(snapshot.selectedClassName);
      }
      if (typeof snapshot.title === 'string') {
        setTitle(snapshot.title);
      }
      if (snapshot.subjectId !== undefined) {
        setSubjectId(snapshot.subjectId);
      }
      if (snapshot.curriculumId !== undefined) {
        setCurriculumId(snapshot.curriculumId);
      }
      if (snapshot.primarySitzplanId !== undefined) {
        setPrimarySitzplanId(snapshot.primarySitzplanId);
      }
      if (typeof snapshot.nfcAutoEnabled === 'boolean') {
        setNfcAutoEnabled(snapshot.nfcAutoEnabled);
      }
      if (typeof snapshot.docAutoEnabled === 'boolean') {
        setDocAutoEnabled(snapshot.docAutoEnabled);
      }
      if (typeof snapshot.startAt === 'string') {
        setStartAt(snapshot.startAt);
      }
      if (typeof snapshot.endAt === 'string') {
        setEndAt(snapshot.endAt);
      }
      if (Array.isArray(snapshot.contextStudents)) {
        setContextStudents(snapshot.contextStudents);
      }
      if (typeof snapshot.contextError === 'string' || snapshot.contextError === null) {
        setContextError(snapshot.contextError);
      }

      if (snapshot.quickstartContextType) {
        setQuickstartContextType(snapshot.quickstartContextType);
      }
      if (typeof snapshot.quickstartClassName === 'string') {
        setQuickstartClassName(snapshot.quickstartClassName);
      }
      if (snapshot.quickstartCourseId !== undefined) {
        setQuickstartCourseId(snapshot.quickstartCourseId);
      }
      if (snapshot.quickstartGroupId !== undefined) {
        setQuickstartGroupId(snapshot.quickstartGroupId);
      }
      if (snapshot.quickstartSubjectId !== undefined) {
        setQuickstartSubjectId(snapshot.quickstartSubjectId);
      }
      if (typeof snapshot.quickstartTheme === 'string') {
        setQuickstartTheme(snapshot.quickstartTheme);
      }
      if (snapshot.quickstartContext) {
        setQuickstartContext(snapshot.quickstartContext);
      }
      if (typeof snapshot.quickstartError === 'string' || snapshot.quickstartError === null) {
        setQuickstartError(snapshot.quickstartError);
      }
      if (snapshot.selectedQuickstartSitzplanId !== undefined) {
        setSelectedQuickstartSitzplanId(snapshot.selectedQuickstartSitzplanId);
      }
      if (Array.isArray(snapshot.selectedCompetencyIds)) {
        setSelectedCompetencyIds(snapshot.selectedCompetencyIds);
      }
      if (snapshot.quickstartTeachingUnitId !== undefined) {
        setQuickstartTeachingUnitId(snapshot.quickstartTeachingUnitId);
      }
      if (snapshot.quickstartTeachingStatus !== undefined) {
        setQuickstartTeachingStatus(snapshot.quickstartTeachingStatus);
      }
      if (
        snapshot.selectedKeywordHint === null ||
        (snapshot.selectedKeywordHint &&
          typeof snapshot.selectedKeywordHint.keyword === 'string' &&
          typeof snapshot.selectedKeywordHint.explanation === 'string')
      ) {
        setSelectedKeywordHint(snapshot.selectedKeywordHint);
      }
      if (snapshot.quickstartBookingCounts && typeof snapshot.quickstartBookingCounts === 'object') {
        setQuickstartBookingCounts(snapshot.quickstartBookingCounts);
      }
      if (Array.isArray(snapshot.plannedMediaItems)) {
        setPlannedMediaItems(snapshot.plannedMediaItems);
      }
      if (snapshot.quickstartAwardedCompetencyCounts && typeof snapshot.quickstartAwardedCompetencyCounts === 'object') {
        setQuickstartAwardedCompetencyCounts(snapshot.quickstartAwardedCompetencyCounts);
      }
      if (Array.isArray(snapshot.quickstartNoSitzplanStudents) || snapshot.quickstartNoSitzplanStudents === null) {
        setQuickstartNoSitzplanStudents(snapshot.quickstartNoSitzplanStudents);
      }
      if (typeof snapshot.quickstartDisplayToastEnabled === 'boolean') {
        setQuickstartDisplayToastEnabled(snapshot.quickstartDisplayToastEnabled);
      }
      if (typeof snapshot.quickstartDisplayRankingToastEnabled === 'boolean') {
        setQuickstartDisplayRankingToastEnabled(snapshot.quickstartDisplayRankingToastEnabled);
      }
      if (typeof snapshot.isLeftPanelCollapsed === 'boolean') {
        setIsLeftPanelCollapsed(snapshot.isLeftPanelCollapsed);
      }
      if (typeof snapshot.isRightPanelCollapsed === 'boolean') {
        setIsRightPanelCollapsed(snapshot.isRightPanelCollapsed);
      }
      if (typeof snapshot.displayPin === 'string') {
        setDisplayPin(snapshot.displayPin);
      }
      if (typeof snapshot.connectedDisplayPin === 'string' || snapshot.connectedDisplayPin === null) {
        setConnectedDisplayPin(snapshot.connectedDisplayPin);
      }
      if (typeof snapshot.showDisplayConnectForm === 'boolean') {
        setShowDisplayConnectForm(snapshot.showDisplayConnectForm);
      }
    } catch (error) {
      console.warn('Failed to restore Unterrichten session state:', error);
    } finally {
      stateRestoreDoneRef.current = true;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadActiveDisplayConnection = async () => {
      try {
        const connection = await fetchQuickstartDisplayConnection();
        if (cancelled) {
          return;
        }
        if (connection?.pin && connection.status === 'connected') {
          setConnectedDisplayPin(connection.pin);
          setDisplayPin(connection.pin);
          setShowDisplayConnectForm(false);
          try {
            await teacherDisableLernbueroDashboard(connection.pin);
          } catch {
            // Display zeigt ggf. weiter Lernbüro; nicht blockieren
          }
        } else {
          setConnectedDisplayPin(null);
          setShowDisplayConnectForm(true);
        }
      } catch (error) {
        if (!cancelled) {
          setConnectedDisplayPin(null);
          setShowDisplayConnectForm(true);
        }
      }
    };

    loadActiveDisplayConnection();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!connectedDisplayPin) {
      return;
    }

    const interval = window.setInterval(async () => {
      try {
        const status = await fetchQuickstartDisplayStatus(connectedDisplayPin);
        if (status.status !== 'connected') {
          setConnectedDisplayPin(null);
          setShowDisplayConnectForm(true);
          setDisplayPin('');
          toast('Display-Verbindung wurde beendet.');
        }
      } catch (error: any) {
        if (error?.response?.status === 404) {
          setConnectedDisplayPin(null);
          setShowDisplayConnectForm(true);
          setDisplayPin('');
        }
      }
    }, 15000);

    return () => {
      window.clearInterval(interval);
    };
  }, [connectedDisplayPin]);

  useEffect(() => {
    let cancelled = false;
    if (unterrichtenMode !== 'structure' || !selectedUnitId) {
      return () => {
        cancelled = true;
      };
    }

    const loadPlannedMediaForUnit = async () => {
      try {
        const state = await fetchTeachingQuickstartState(selectedUnitId);
        if (!cancelled) {
          setPlannedMediaItems(Array.isArray(state?.plannedMediaItems) ? state.plannedMediaItems : []);
        }
      } catch (error) {
        if (!cancelled) {
          setPlannedMediaItems([]);
        }
      }
    };

    loadPlannedMediaForUnit();
    return () => {
      cancelled = true;
    };
  }, [unterrichtenMode, selectedUnitId]);

  const { data: metaData, isLoading: metaLoading } = useQuery<TeachingPreparationMeta>(
    ['unterrichten', 'meta'],
    async () => {
      const meta = await fetchTeachingMeta();
      return meta;
    },
    {
      staleTime: 5 * 60 * 1000
    }
  );

  const { data: classAggregates } = useQuery<ClassAggregate[]>(
    ['unterrichten', 'class-aggregates'],
    async () => {
      const response = await apiClient.get('/students/classes');
      return response.data?.data ?? [];
    },
    {
      staleTime: 5 * 60 * 1000
    }
  );

  const { data: teachingUnits, isLoading: unitsLoading, isFetching: unitsFetching } = useQuery<TeachingUnitSummary[]>(
    ['unterrichten', 'units', statusFilter, listScope],
    async () => {
      const params: { status?: TeachingUnitSummary['status']; inTrash?: boolean } = {
        inTrash: listScope === 'trash'
      };
      if (listScope === 'archive') {
        params.status = 'archived';
      } else if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const list = await fetchTeachingUnits(params);
      return list;
    },
    {
      onError: (error: any) => {
        const message = error?.response?.data?.error || 'Unterrichtseinheiten konnten nicht geladen werden';
        toast.error(message);
      }
    }
  );

  const { data: selectedUnit, isLoading: detailLoading } = useQuery<TeachingUnitDetail | null>(
    ['unterrichten', 'detail', selectedUnitId],
    async () => {
      if (!selectedUnitId) {
        return null;
      }
      return await fetchTeachingUnitDetail(selectedUnitId);
    },
    {
      enabled: !!selectedUnitId
    }
  );

  const { data: quickstartActiveAbsencesResponse, refetch: refetchQuickstartActiveAbsences } = useQuery(
    ['temporary-absences-active', 'teaching_unit', quickstartTeachingUnitId],
    () => fetchActiveAbsences('teaching_unit', quickstartTeachingUnitId!),
    { enabled: !!quickstartTeachingUnitId }
  );
  const quickstartActiveAbsencesMap = quickstartActiveAbsencesResponse?.data ?? {};

  const persistPlannedMediaItemsForUnit = useCallback(
    async (unitId: number, items: QuickstartPlannedMediaItem[]) => {
      try {
        const currentState = await fetchTeachingQuickstartState(unitId);
        await saveTeachingQuickstartState(unitId, {
          selectedCompetencyIds: currentState?.selectedCompetencyIds || [],
          selectedKeywordHint: currentState?.selectedKeywordHint || null,
          bookingCounts: currentState?.bookingCounts || {},
          keywordHints: currentState?.keywordHints || [],
          plannedMediaItems: items
        });
      } catch (error) {
        console.warn('Failed to persist planned media items:', error);
      }
    },
    []
  );

  const createMutation = useMutation(
    async (payload: TeachingUnitCreatePayload) => {
      return await createTeachingUnitApi(payload);
    },
    {
      onSuccess: async (result: any) => {
        const createdUnitId = Number(result?.id);
        if (createdUnitId) {
          setSelectedUnitId(createdUnitId);
        }
        if (createdUnitId && plannedMediaItems.length > 0) {
          await persistPlannedMediaItemsForUnit(createdUnitId, plannedMediaItems);
        }
        toast.success('Unterrichtseinheit erstellt');
        queryClient.invalidateQueries(['unterrichten', 'units']);
        setTitle('');
        setParentType('class');
        setSelectedParentId('');
        setSelectedClassName('');
        setSubjectId('');
        setCurriculumId('');
        setPrimarySitzplanId('');
        setNfcAutoEnabled(false);
        setDocAutoEnabled(false);
        setStartAt('');
        setEndAt('');
      },
      onError: (error: any) => {
        console.error('Error creating teaching unit:', error);
        const message = error?.response?.data?.error || 'Unterrichtseinheit konnte nicht erstellt werden';
        toast.error(message);
      }
    }
  );

  const updateMutation = useMutation(
    async ({
      id,
      payload
    }: {
      id: number;
      payload: {
        status?: TeachingUnitSummary['status'];
        start_at?: string;
        end_at?: string;
        series_name?: string | null;
      };
    }) => {
      return await updateTeachingUnitApi(id, payload);
    },
    {
      onSuccess: (_, variables) => {
        toast.success('Unterrichtseinheit aktualisiert');
        queryClient.invalidateQueries(['unterrichten', 'units']);
        queryClient.invalidateQueries(['unterrichten', 'detail', variables.id]);
      },
      onError: (error: any) => {
        console.error('Error updating teaching unit:', error);
        const message = error?.response?.data?.error || 'Aktualisierung fehlgeschlagen';
        toast.error(message);
      }
    }
  );

  useEffect(() => {
    let cancelled = false;

    const resetContext = () => {
      if (!cancelled) {
        setContextStudents([]);
        setContextError(null);
        setContextLoading(false);
      }
    };

    const loadContextStudents = async () => {
      if (parentType === 'ad_hoc') {
        resetContext();
        return;
      }

      if (parentType === 'class' && !selectedClassName) {
        resetContext();
        return;
      }

      if ((parentType === 'course' || parentType === 'group') && !selectedParentId) {
        resetContext();
        return;
      }

      setContextLoading(true);
      setContextError(null);

      try {
        let students: ContextStudent[] = [];

        if (parentType === 'class' && selectedClassName) {
          const response = await apiClient.get(
            `/assignments/class/${encodeURIComponent(selectedClassName)}/students`
          );
          const data = response.data?.data;
          students = (data?.students ?? []).map((student: any) => ({
            id: student.sophomorixUnid || String(student.id),
            firstName: student.firstName || student.Vorname || '',
            lastName: student.lastName || student.Name || '',
            className: student.className || student.Klasse || selectedClassName
          }));
        } else if (parentType === 'course' && selectedParentId) {
          const response = await apiClient.get(`/courses/${selectedParentId}/participants`);
          const data = response.data?.data ?? [];
          students = data.map((participant: any) => ({
            id: participant.id,
            firstName: participant.first_name || '',
            lastName: participant.last_name || '',
            className: participant.klasse || null
          }));
        } else if (parentType === 'group' && selectedParentId) {
          const response = await apiClient.get(`/groups/${selectedParentId}/participants`);
          const data = response.data?.data ?? [];
          students = data.map((participant: any) => ({
            id: participant.id,
            firstName: participant.first_name || '',
            lastName: participant.last_name || '',
            className: participant.klasse || null
          }));
        }

        if (!cancelled) {
          setContextStudents(students);
        }
      } catch (error: any) {
        console.error('Error fetching context students:', error);
        if (!cancelled) {
          const message = error?.response?.data?.message || 'Teilnehmer konnten nicht geladen werden';
          setContextError(message);
          setContextStudents([]);
        }
      } finally {
        if (!cancelled) {
          setContextLoading(false);
        }
      }
    };

    loadContextStudents();

    return () => {
      cancelled = true;
    };
  }, [parentType, selectedParentId, selectedClassName]);

  const classesFromMeta = metaData?.classes ?? [];
  const coursesFromMeta = metaData?.courses ?? [];
  const groupsFromMeta = metaData?.groups ?? [];
  const subjectsFromMeta = metaData?.subjects ?? [];
  const curriculaFromMeta = metaData?.curricula ?? [];
  const sitzplaeneFromMeta = metaData?.sitzplaene ?? [];

  const selectableSitzplaene = useMemo(() => {
    if (!sitzplaeneFromMeta.length) {
      return [];
    }
    if (parentType === 'ad_hoc') {
      return sitzplaeneFromMeta;
    }
    if (parentType === 'class') {
      if (!selectedClassName) {
        return sitzplaeneFromMeta.filter((plan: any) => !plan.context_type);
      }
      return sitzplaeneFromMeta.filter((plan: any) => {
        if (plan.context_type === 'class') {
          return String(plan.context_value || '').trim() === String(selectedClassName).trim();
        }
        // Backward compatibility for older plans without context fields
        return !plan.context_type && plan.klasse === selectedClassName;
      });
    }
    if ((parentType === 'course' || parentType === 'group') && selectedParentId) {
      return sitzplaeneFromMeta.filter(
        (plan: any) =>
          plan.context_type === parentType && String(plan.context_value || '') === String(selectedParentId)
      );
    }
    return sitzplaeneFromMeta;
  }, [sitzplaeneFromMeta, parentType, selectedClassName, selectedParentId]);

  const classOptions = useMemo(() => {
    if (!classesFromMeta.length) {
      return [];
    }
    return classesFromMeta.map((cls: any) => {
      const value = cls.id !== null && cls.id !== undefined ? String(cls.id) : cls.class_name;
      const countText = cls.student_count ? `${cls.student_count} Schüler` : undefined;
      return {
        value,
        label: cls.class_name,
        description: countText ? `${cls.class_name} · ${countText}` : cls.class_name,
        studentCount: cls.student_count ?? undefined
      };
    });
  }, [classesFromMeta]);

  const courseOptions = useMemo(() => {
    return coursesFromMeta.map((course: any) => ({
      value: course.id,
      label: course.name,
      description: [course.fach, course.jahrgang, course.schuljahr].filter(Boolean).join(' · ')
    }));
  }, [coursesFromMeta]);

  const groupOptions = useMemo(() => {
    return groupsFromMeta.map((group: any) => ({
      value: group.id,
      label: group.name,
      description: group.gruppen_typ ? `Typ: ${group.gruppen_typ}` : group.name
    }));
  }, [groupsFromMeta]);

  const selectedStatus = useMemo(() => {
    if (!selectedUnit?.unit) {
      return undefined;
    }
    return selectedUnit.unit.status;
  }, [selectedUnit]);

  const seriesOptions = useMemo(() => {
    const names = new Set<string>();
    (teachingUnits || []).forEach((unit) => {
      if (unit.series_name && unit.series_name.trim()) {
        names.add(unit.series_name.trim());
      }
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'de'));
  }, [teachingUnits]);

  const groupedTeachingUnits = useMemo(() => {
    const baseUnits = (teachingUnits || []).filter((unit) => {
      if (listScope === 'normal') {
        return unit.status !== 'archived';
      }
      if (listScope === 'archive') {
        return unit.status === 'archived';
      }
      return true;
    });

    const groups = new Map<string, TeachingUnitSummary[]>();
    baseUnits.forEach((unit) => {
      const key = unit.series_name?.trim() || 'Ohne Reihe';
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(unit);
    });
    return Array.from(groups.entries()).sort(([a], [b]) => {
      if (a === 'Ohne Reihe') {
        return 1;
      }
      if (b === 'Ohne Reihe') {
        return -1;
      }
      return a.localeCompare(b, 'de');
    });
  }, [teachingUnits, listScope]);

  const subjectLabelById = useMemo(() => {
    const map = new Map<number, string>();
    subjectsFromMeta.forEach((subject: any) => {
      if (subject?.id) {
        map.set(Number(subject.id), subject.name);
      }
    });
    return map;
  }, [subjectsFromMeta]);

  const listScopeLabel = useMemo(() => {
    if (listScope === 'trash') {
      return 'Papierkorb';
    }
    if (listScope === 'archive') {
      return 'Archiv';
    }
    return 'Normalansicht';
  }, [listScope]);

  const isQuickstartSessionActive = useMemo(() => !!quickstartTeachingUnitId, [quickstartTeachingUnitId]);

  const classLabelByKey = useMemo(() => {
    const map = new Map<string, string>();
    classOptions.forEach((option: any) => {
      map.set(String(option.value), option.label);
      map.set(String(option.label), option.label);
    });
    return map;
  }, [classOptions]);

  const courseLabelById = useMemo(() => {
    const map = new Map<string, string>();
    courseOptions.forEach((option: any) => {
      map.set(String(option.value), option.label);
    });
    return map;
  }, [courseOptions]);

  const groupLabelById = useMemo(() => {
    const map = new Map<string, string>();
    groupOptions.forEach((option: any) => {
      map.set(String(option.value), option.label);
    });
    return map;
  }, [groupOptions]);

  const classStudentCount = useMemo(() => {
    if (parentType === 'ad_hoc') {
      return undefined;
    }
    if (contextStudents.length > 0) {
      return contextStudents.length;
    }
    if (parentType === 'class') {
      const option = classOptions.find((cls: any) => cls.value === selectedParentId || cls.label === selectedClassName);
      if (option?.studentCount !== undefined) {
        return option.studentCount;
      }
    }
    if (selectedClassName && classAggregates) {
      const entry = classAggregates.find((cls) => cls.Klasse === selectedClassName);
      if (entry?.student_count !== undefined) {
        return entry.student_count;
      }
    }
    return undefined;
  }, [parentType, contextStudents, classOptions, selectedParentId, selectedClassName, classAggregates]);

  const quickstartStudents = useMemo<QuickstartStudentView[]>(() => {
    if (!quickstartContext?.students) {
      return [];
    }
    return quickstartContext.students.map((student) => ({
      id: student.sophomorix_unid || student.id,
      firstName: student.first_name || '',
      lastName: student.last_name || '',
      className: student.class_name || quickstartContext.className
    }));
  }, [quickstartContext]);

  const quickstartStudentsAlphabetical = useMemo<QuickstartStudentView[]>(() => {
    return [...quickstartStudents].sort((a, b) => {
      const lastNameCompare = (a.lastName || '').localeCompare(b.lastName || '', 'de', { sensitivity: 'base' });
      if (lastNameCompare !== 0) {
        return lastNameCompare;
      }
      return (a.firstName || '').localeCompare(b.firstName || '', 'de', { sensitivity: 'base' });
    });
  }, [quickstartStudents]);

  const quickstartContextLabel = useMemo(() => {
    if (quickstartContextType === 'course') {
      return courseOptions.find((course: any) => Number(course.value) === Number(quickstartCourseId))?.label || '';
    }
    if (quickstartContextType === 'group') {
      return groupOptions.find((group: any) => Number(group.value) === Number(quickstartGroupId))?.label || '';
    }
    return quickstartClassName;
  }, [quickstartContextType, quickstartClassName, quickstartCourseId, quickstartGroupId, courseOptions, groupOptions]);

  const quickstartFallbackStudents = useMemo<QuickstartStudentView[]>(() => {
    if (quickstartNoSitzplanStudents && quickstartNoSitzplanStudents.length > 0) {
      return [...quickstartNoSitzplanStudents].sort((a, b) => {
        const lastNameCompare = (a.lastName || '').localeCompare(b.lastName || '', 'de', { sensitivity: 'base' });
        if (lastNameCompare !== 0) {
          return lastNameCompare;
        }
        return (a.firstName || '').localeCompare(b.firstName || '', 'de', { sensitivity: 'base' });
      });
    }
    return quickstartStudentsAlphabetical;
  }, [quickstartNoSitzplanStudents, quickstartStudentsAlphabetical]);

  const quickstartSelectedSitzplan = useMemo(() => {
    if (!quickstartContext || !selectedQuickstartSitzplanId) {
      return null;
    }
    return (
      quickstartContext.sitzplanSuggestions.find((plan) => plan.id === selectedQuickstartSitzplanId) ?? null
    );
  }, [quickstartContext, selectedQuickstartSitzplanId]);

  const quickstartCompactGrid = useMemo(() => {
    if (!quickstartSelectedSitzplan) {
      return { rows: [], cols: [], cells: [] as Array<{ row: number; col: number }> };
    }

    const occupied = quickstartSelectedSitzplan.positionen.filter((pos) => !!pos?.sophomorix_unid);
    if (occupied.length === 0) {
      const fallbackRows = Array.from(
        { length: Math.max(1, quickstartSelectedSitzplan.grid_rows || 1) },
        (_, idx) => idx
      );
      const fallbackCols = Array.from(
        { length: Math.max(1, quickstartSelectedSitzplan.grid_cols || 1) },
        (_, idx) => idx
      );
      return {
        rows: fallbackRows,
        cols: fallbackCols,
        cells: fallbackRows.flatMap((row) => fallbackCols.map((col) => ({ row, col })))
      };
    }

    // Remove ALL empty rows/cols, not only outer margins.
    const usedRows = Array.from(new Set(occupied.map((pos) => pos.grid_row))).sort((a, b) => a - b);
    const usedCols = Array.from(new Set(occupied.map((pos) => pos.grid_col))).sort((a, b) => a - b);

    return {
      rows: usedRows,
      cols: usedCols,
      cells: usedRows.flatMap((row) => usedCols.map((col) => ({ row, col })))
    };
  }, [quickstartSelectedSitzplan]);

  const quickstartStudentMap = useMemo(() => {
    const map = new Map<string, QuickstartStudentView>();
    quickstartStudents.forEach((student) => {
      map.set(String(student.id), student);
    });
    return map;
  }, [quickstartStudents]);

  const quickstartLayoutColumns = useMemo(() => {
    if (isLeftPanelCollapsed && isRightPanelCollapsed) {
      return '2.5rem minmax(0,1fr) 2.5rem';
    }
    if (isLeftPanelCollapsed) {
      return '2.5rem minmax(0,1fr) 18rem';
    }
    if (isRightPanelCollapsed) {
      return '19rem minmax(0,1fr) 2.5rem';
    }
    return '19rem minmax(0,1fr) 18rem';
  }, [isLeftPanelCollapsed, isRightPanelCollapsed]);

  const quickstartWorkspaceHeight = useViewportRemainingHeight(quickstartWorkspaceAnchorRef, 320, 16);

  const quickstartStudentGridSpec = useMemo<FitSquareGridSpec | null>(() => {
    if (quickstartCenterView !== 'students') {
      return null;
    }
    if (quickstartSelectedSitzplan && quickstartCompactGrid.cols.length > 0) {
      return {
        mode: 'fixed',
        cols: quickstartCompactGrid.cols.length,
        rows: quickstartCompactGrid.rows.length
      };
    }
    if (quickstartFallbackStudents.length > 0) {
      return {
        mode: 'auto',
        itemCount: quickstartFallbackStudents.length
      };
    }
    return null;
  }, [
    quickstartCenterView,
    quickstartSelectedSitzplan,
    quickstartCompactGrid.cols.length,
    quickstartCompactGrid.rows.length,
    quickstartFallbackStudents.length
  ]);

  const quickstartStudentGridLayout = useFitSquareGridSize(quickstartStudentsGridRef, quickstartStudentGridSpec, {
    gap: QUICKSTART_STUDENT_TILE_GAP,
    padding: QUICKSTART_STUDENT_GRID_PADDING,
    minTileSize: 56
  });

  const loadQuickstartContext = useCallback(async (
    options: { silent?: boolean } = {}
  ): Promise<TeachingQuickstartContext | null> => {
    const requiresClass = quickstartContextType === 'class';
    const requiresCourse = quickstartContextType === 'course';
    const requiresGroup = quickstartContextType === 'group';
    if (
      (!quickstartSubjectId || !quickstartTheme.trim()) ||
      (requiresClass && !quickstartClassName) ||
      (requiresCourse && !quickstartCourseId) ||
      (requiresGroup && !quickstartGroupId)
    ) {
      if (!options.silent) {
        toast.error('Bitte Kontext, Auswahl, Fach und Thema wählen');
      }
      return null;
    }

    setQuickstartLoading(true);
    setQuickstartError(null);
    setQuickstartTeachingUnitId(null);
    setQuickstartTeachingStatus(null);
    setQuickstartNoSitzplanStudents(null);
    setPlannedMediaItems([]);

    try {
      const normalizeParticipants = (participants: any[]) =>
        (participants || [])
          .map((participant) => ({
            id: String(participant.id || participant.student_id || ''),
            sophomorix_unid: String(participant.sophomorix_unid || participant.id || participant.student_id || ''),
            first_name: participant.first_name || participant.Vorname || '',
            last_name: participant.last_name || participant.Name || '',
            class_name: participant.class_name || participant.klasse || participant.Klasse || ''
          }))
          .filter((student) => student.id);

      let contextStudentsOverride: Array<{
        id: string;
        sophomorix_unid: string;
        first_name: string;
        last_name: string;
        class_name: string;
      }> | null = null;

      if (quickstartContextType === 'course' && quickstartCourseId) {
        const response = await apiClient.get(`/courses/${quickstartCourseId}/participants`);
        contextStudentsOverride = normalizeParticipants((response.data?.data ?? []) as any[]);
        if (contextStudentsOverride.length === 0) {
          const fallback = await apiClient.get('/courses-groups/students', {
            params: {
              unitType: 'course',
              unitId: quickstartCourseId,
              onlyAvailable: 'false',
              limit: 2000,
              offset: 0
            }
          });
          contextStudentsOverride = normalizeParticipants((fallback.data?.data ?? []) as any[]);
        }
      } else if (quickstartContextType === 'group' && quickstartGroupId) {
        const response = await apiClient.get(`/groups/${quickstartGroupId}/participants`);
        contextStudentsOverride = normalizeParticipants((response.data?.data ?? []) as any[]);
        if (contextStudentsOverride.length === 0) {
          const fallback = await apiClient.get('/courses-groups/students', {
            params: {
              unitType: 'group',
              unitId: quickstartGroupId,
              onlyAvailable: 'false',
              limit: 2000,
              offset: 0
            }
          });
          contextStudentsOverride = normalizeParticipants((fallback.data?.data ?? []) as any[]);
        }
      }

      let classNameForContext = quickstartClassName;
      if (contextStudentsOverride && contextStudentsOverride.length > 0) {
        const classCounts = new Map<string, number>();
        contextStudentsOverride.forEach((student) => {
          const cls = String(student.class_name || '').trim();
          if (!cls) {
            return;
          }
          classCounts.set(cls, (classCounts.get(cls) || 0) + 1);
        });
        const bestClass = Array.from(classCounts.entries()).sort((a, b) => b[1] - a[1])[0]?.[0];
        classNameForContext = bestClass || quickstartClassName || quickstartContextLabel || 'Kontext';
      } else if (quickstartContextType !== 'class') {
        classNameForContext = quickstartClassName || quickstartContextLabel || 'Kontext';
      }

      const context = await fetchTeachingQuickstartContext({
        className: classNameForContext,
        subjectId: Number(quickstartSubjectId),
        theme: quickstartTheme.trim()
      });
      const mergedContext = contextStudentsOverride
        ? {
            ...context,
            className: classNameForContext,
            students: contextStudentsOverride
          }
        : context;
      setQuickstartContext(mergedContext);
      setSelectedQuickstartSitzplanId(context.suggestedSitzplanId ?? null);
      setSelectedCompetencyIds(
        context.defaultSelectedCompetencyIds?.length
          ? context.defaultSelectedCompetencyIds
          : context.competencySuggestions.map((competency) => competency.id)
      );
      setSelectedKeywordHint(
        Array.isArray(context.keywordHints) && context.keywordHints.length > 0 ? context.keywordHints[0] : null
      );
      setQuickstartBookingCounts({});
      setQuickstartAwardedCompetencyCounts({});
      if (contextStudentsOverride) {
        setQuickstartNoSitzplanStudents(
          contextStudentsOverride.map((student) => ({
            id: student.sophomorix_unid || student.id,
            firstName: student.first_name || '',
            lastName: student.last_name || '',
            className: student.class_name || classNameForContext
          }))
        );
      }
      if (!options.silent) {
        toast.success('Schnellstart vorbereitet');
      }
      return mergedContext;
    } catch (error: any) {
      console.error('Error loading quickstart context:', error);
      const message =
        error?.response?.data?.error || error?.response?.data?.message || 'Schnellstart konnte nicht geladen werden';
      setQuickstartError(message);
      setQuickstartContext(null);
      if (!options.silent) {
        toast.error(message);
      }
      return null;
    } finally {
      setQuickstartLoading(false);
    }
  }, [
    quickstartContextType,
    quickstartClassName,
    quickstartCourseId,
    quickstartGroupId,
    quickstartSubjectId,
    quickstartTheme,
    quickstartContextLabel
  ]);

  const startQuickstartTeaching = useCallback(async (
    contextOverride?: TeachingQuickstartContext,
    competencyIdsOverride?: number[],
    options: { silent?: boolean } = {}
  ): Promise<number | null> => {
    const context = contextOverride || quickstartContext;
    const competencyIds = competencyIdsOverride || selectedCompetencyIds;

    if (!context) {
      if (!options.silent) {
        toast.error('Bitte zuerst den Schnellstart laden');
      }
      return null;
    }
    if (competencyIds.length === 0) {
      if (!options.silent) {
        toast.error('Bitte mindestens eine Kompetenz auswählen');
      }
      return null;
    }

    const subjectLabel = subjectsFromMeta.find((subject: any) => subject.id === Number(quickstartSubjectId))?.name;
    const titleParts = [subjectLabel, quickstartContextLabel, quickstartTheme.trim()].filter(Boolean);
    const quickstartParentType = quickstartContextType;
    const quickstartParentId =
      quickstartParentType === 'course'
        ? Number(quickstartCourseId)
        : quickstartParentType === 'group'
        ? Number(quickstartGroupId)
        : undefined;
    const participants = context.students.map((student) => ({
      student_id: student.sophomorix_unid || student.id,
      origin: quickstartParentType as 'class' | 'course' | 'group',
      origin_reference: quickstartContextLabel,
      is_active: true
    }));
    if (participants.length === 0) {
      if (!options.silent) {
        toast.error('Keine Teilnehmer im gewählten Kontext gefunden');
      }
      return null;
    }

    const payload: TeachingUnitCreatePayload = {
      title: titleParts.join(' – '),
      status: 'active',
      parent_type: quickstartParentType,
      parent_id: Number.isFinite(quickstartParentId as number) ? quickstartParentId : undefined,
      subject_id: Number(quickstartSubjectId),
      primary_sitzplan_id: (context.suggestedSitzplanId || selectedQuickstartSitzplanId) || undefined,
      participants,
      competencies: competencyIds.map((competencyId) => ({
        competency_id: competencyId,
        is_focus: true
      }))
    };

    try {
      const result = await createTeachingUnitApi(payload);
      const unitId = Number(result?.id);
      if (!unitId) {
        throw new Error('No teaching unit id returned');
      }
      setQuickstartTeachingUnitId(unitId);
      setQuickstartTeachingStatus('active');
      setQuickstartBookingCounts({});
      setQuickstartAwardedCompetencyCounts({});
      setQuickstartNoSitzplanStudents(null);
      queryClient.invalidateQueries(['unterrichten', 'units']);
      if (!options.silent) {
        toast.success('Unterricht direkt gestartet');
      }
      return unitId;
    } catch (error: any) {
      console.error('Error starting quickstart teaching unit:', error);
      const message =
        error?.response?.data?.error || error?.response?.data?.message || 'Unterricht konnte nicht gestartet werden';
      if (!options.silent) {
        toast.error(message);
      }
      return null;
    }
  }, [
    quickstartContext,
    selectedCompetencyIds,
    subjectsFromMeta,
    quickstartSubjectId,
    quickstartContextType,
    quickstartContextLabel,
    quickstartClassName,
    quickstartCourseId,
    quickstartGroupId,
    quickstartTheme,
    selectedQuickstartSitzplanId,
    queryClient
  ]);

  const handleQuickstartStart = async () => {
    if (quickstartTeachingUnitId) {
      try {
        await updateTeachingUnitApi(quickstartTeachingUnitId, { status: 'active' });
        setQuickstartTeachingStatus('active');
        queryClient.invalidateQueries(['unterrichten', 'units']);
        toast.success('Unterricht fortgesetzt');
      } catch (error: any) {
        const message =
          error?.response?.data?.error || error?.response?.data?.message || 'Unterricht konnte nicht gestartet werden';
        toast.error(message);
      }
      return;
    }

    const context = await loadQuickstartContext();
    if (!context) {
      return;
    }
    const defaultIds =
      context.defaultSelectedCompetencyIds?.length
        ? context.defaultSelectedCompetencyIds
        : context.competencySuggestions.map((competency) => competency.id);
    await startQuickstartTeaching(context, defaultIds);
  };

  const handleConnectDisplay = async () => {
    const pin = displayPin.trim().toUpperCase();
    if (!/^[A-Z0-9]{6}$/.test(pin)) {
      toast.error('Bitte eine gültige 6-stellige PIN (A-Z, 0-9) eingeben');
      return;
    }

    setDisplayConnectLoading(true);
    try {
      if (connectedDisplayPin && connectedDisplayPin !== pin) {
        await disconnectQuickstartDisplay(connectedDisplayPin);
      }
      await connectQuickstartDisplay({
        pin,
        teachingUnitId: quickstartTeachingUnitId ?? undefined,
        className: quickstartContextLabel || quickstartClassName || undefined,
        theme: quickstartTheme || undefined
      });
      setConnectedDisplayPin(pin);
      setShowDisplayConnectForm(false);
      toast.success(`Display ${pin} verbunden`);
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.response?.data?.message || 'Display konnte nicht verbunden werden';
      toast.error(message);
    } finally {
      setDisplayConnectLoading(false);
    }
  };

  const handleDisconnectDisplay = async () => {
    if (!connectedDisplayPin) {
      return;
    }
    const confirmed = window.confirm(`Display ${connectedDisplayPin} wirklich entkoppeln?`);
    if (!confirmed) {
      return;
    }

    setDisplayDisconnectLoading(true);
    try {
      await disconnectQuickstartDisplay(connectedDisplayPin);
      setConnectedDisplayPin(null);
      setShowDisplayConnectForm(true);
      setDisplayPin('');
      toast.success('Display-Kopplung entfernt');
    } catch (error: any) {
      const message =
        error?.response?.data?.error || error?.response?.data?.message || 'Display-Kopplung konnte nicht entfernt werden';
      toast.error(message);
    } finally {
      setDisplayDisconnectLoading(false);
    }
  };

  const loadDisplaySnapshots = async () => {
    try {
      const list = await listDisplaySnapshots({
        teachingUnitId: quickstartTeachingUnitId || undefined,
        favoritesOnly: snapshotFavoritesOnly,
        q: snapshotSearchInput.trim() || undefined
      });
      setDisplaySnapshots(list);
    } catch {
      setDisplaySnapshots([]);
    }
  };

  useEffect(() => {
    if (!showSnapshotPanel) return;
    void loadDisplaySnapshots();
  }, [showSnapshotPanel, quickstartTeachingUnitId, snapshotFavoritesOnly, snapshotSearchInput]);

  const handleSaveDisplaySnapshot = async () => {
    if (!connectedDisplayPin) {
      toast.error('Bitte zuerst ein Display koppeln');
      return;
    }
    setSnapshotSaving(true);
    try {
      const result = await saveDisplaySnapshot({
        pin: connectedDisplayPin,
        name: snapshotNameInput.trim() || `Snapshot ${new Date().toLocaleString('de-DE')}`,
        teachingUnitId: quickstartTeachingUnitId || undefined,
        folderName: snapshotFolderInput.trim() || undefined
      });
      toast.success(`Tafelbild gespeichert (${result.pageCount} Seiten, ${result.itemCount} Objekte auf aktiver Seite)`);
      setSnapshotNameInput('');
      setSnapshotFolderInput('');
      void loadDisplaySnapshots();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Snapshot konnte nicht gespeichert werden');
    } finally {
      setSnapshotSaving(false);
    }
  };

  const handleRestoreDisplaySnapshot = async (snapshotId: number) => {
    if (!connectedDisplayPin) {
      toast.error('Bitte zuerst ein Display koppeln');
      return;
    }
    setSnapshotRestoring(snapshotId);
    try {
      const result = await restoreDisplaySnapshot(snapshotId, connectedDisplayPin);
      toast.success(`Snapshot wiederhergestellt (${result.itemCount} Objekte)`);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Snapshot konnte nicht wiederhergestellt werden');
    } finally {
      setSnapshotRestoring(null);
    }
  };

  const handleDeleteDisplaySnapshot = async (snapshotId: number) => {
    if (!window.confirm('Diesen Snapshot wirklich löschen?')) return;
    try {
      await deleteDisplaySnapshot(snapshotId);
      setDisplaySnapshots((prev) => prev.filter((s) => s.id !== snapshotId));
      toast.success('Snapshot gelöscht');
    } catch {
      toast.error('Snapshot konnte nicht gelöscht werden');
    }
  };

  const handleToggleSnapshotFavorite = async (snapshotId: number, isFavorite: boolean) => {
    try {
      await updateDisplaySnapshot(snapshotId, { isFavorite: !isFavorite });
      setDisplaySnapshots((prev) =>
        prev.map((snap) => (snap.id === snapshotId ? { ...snap, isFavorite: !isFavorite } : snap))
      );
    } catch {
      toast.error('Favorit konnte nicht aktualisiert werden');
    }
  };

  const handlePresentImageOnDisplay = async (payload: {
    imageUrl: string;
    title?: string;
    sourcePageUrl?: string;
  }) => {
    if (!connectedDisplayPin) {
      toast.error('Bitte zuerst ein Display koppeln');
      return;
    }
    try {
      await presentQuickstartDisplayImage({
        pin: connectedDisplayPin,
        imageUrl: payload.imageUrl,
        title: payload.title,
        sourcePageUrl: payload.sourcePageUrl
      });
      toast.success('Bild wird auf dem Display angezeigt');
    } catch (error: any) {
      const message =
        error?.response?.data?.error || error?.response?.data?.message || 'Bild konnte nicht ans Display gesendet werden';
      toast.error(message);
    }
  };

  const handleAddImageToPlannedMedia = (payload: {
    mediaUrl: string;
    title?: string;
    mimeType?: string;
    sourcePageUrl?: string;
  }) => {
    if (!payload.mediaUrl) {
      return;
    }
    const inferredType = inferPlannedMediaType({ mimeType: payload.mimeType, url: payload.mediaUrl });
    const normalizedMediaUrl =
      inferredType === 'youtube' ? toYouTubeEmbedUrl(payload.mediaUrl) : payload.mediaUrl;
    const itemId = createQuickstartMediaId();
    const fallbackTitle = inferredType === 'youtube' ? 'YouTube-Video …' : (quickstartTheme || 'Bild');
    const item: QuickstartPlannedMediaItem = {
      id: itemId,
      type: 'media',
      title: payload.title || fallbackTitle,
      mediaUrl: normalizedMediaUrl,
      mediaType: inferredType,
      mimeType: payload.mimeType || 'image/*',
      sourcePageUrl: payload.sourcePageUrl || payload.mediaUrl,
      createdAt: new Date().toISOString()
    };
    setPlannedMediaItems((prev) => {
      const alreadyExists = prev.some(
        (entry) => entry.type === 'media' && (entry.mediaUrl || entry.imageUrl) === item.mediaUrl
      );
      if (alreadyExists) {
        return prev;
      }
      const next = [item, ...prev].slice(0, 60);
      if (unterrichtenMode === 'structure' && selectedUnitId) {
        void persistPlannedMediaItemsForUnit(selectedUnitId, next);
      }
      return next;
    });
    // Asynchronously fetch the real YouTube video title
    if (inferredType === 'youtube' && !payload.title) {
      fetchYouTubeTitle(payload.mediaUrl).then(({ title }) => {
        if (!title) return;
        setPlannedMediaItems((prev) => {
          const idx = prev.findIndex((entry) => entry.id === itemId);
          if (idx === -1) return prev;
          const updated = [...prev];
          updated[idx] = { ...updated[idx], title };
          if (unterrichtenMode === 'structure' && selectedUnitId) {
            void persistPlannedMediaItemsForUnit(selectedUnitId, updated);
          }
          return updated;
        });
      });
    }
    toast.success('Medium im Mediendepot hinterlegt');
  };

  const handleRemovePlannedMediaItem = (itemId: string) => {
    setPlannedMediaItems((prev) => {
      const next = prev.filter((item) => item.id !== itemId);
      if (unterrichtenMode === 'structure' && selectedUnitId) {
        void persistPlannedMediaItemsForUnit(selectedUnitId, next);
      }
      return next;
    });
  };

  const handleAddTextToPlannedMedia = (payload: { title?: string; text: string }) => {
    const trimmedText = String(payload.text || '').trim();
    if (!trimmedText) {
      return;
    }
    const item: QuickstartPlannedMediaItem = {
      id: createQuickstartMediaId(),
      type: 'text',
      title: payload.title || 'Textkarte',
      text: trimmedText,
      createdAt: new Date().toISOString()
    };
    setPlannedMediaItems((prev) => {
      const next = [item, ...prev].slice(0, 60);
      if (unterrichtenMode === 'structure' && selectedUnitId) {
        void persistPlannedMediaItemsForUnit(selectedUnitId, next);
      }
      return next;
    });
    toast.success('Text im Mediendepot hinterlegt');
  };

  const handleUploadPlannedMediaFile = async (file?: File | null) => {
    if (!file) {
      return;
    }
    const mime = String(file.type || '').toLowerCase();
    const isAllowed = mime.startsWith('image/') || mime.startsWith('video/') || mime === 'application/pdf';
    if (!isAllowed) {
      toast.error('Nur Bild-, Video- oder PDF-Dateien sind erlaubt');
      return;
    }
    const maxBytes = 200 * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error('Datei ist zu groß (max. 200 MB)');
      return;
    }
    try {
      setPlannedMediaUploading(true);
      const uploaded = await uploadTeachingMediaFile(
        file,
        plannedMediaTitleInput.trim() || file.name,
        unterrichtenMode === 'structure' ? selectedUnitId : quickstartTeachingUnitId
      );
      const fallbackTitle = file.name.replace(/\.[^/.]+$/, '');
      const mediaItem: QuickstartPlannedMediaItem = {
        id: String(uploaded?.id || createQuickstartMediaId()),
        assetId: uploaded?.assetId ? Number(uploaded.assetId) : null,
        type: 'media',
        title: String(uploaded?.title || plannedMediaTitleInput.trim() || fallbackTitle || file.name),
        mediaUrl: String(uploaded?.mediaUrl || ''),
        mediaType: (uploaded?.mediaType as 'image' | 'video' | 'pdf' | 'youtube') || (mime.startsWith('video/') ? 'video' : mime === 'application/pdf' ? 'pdf' : 'image'),
        mimeType: String(uploaded?.mimeType || mime),
        sourcePageUrl: String(uploaded?.sourcePageUrl || uploaded?.mediaUrl || ''),
        originalName: String(uploaded?.originalName || file.name || ''),
        uploadedAt: uploaded?.uploadedAt ? String(uploaded.uploadedAt) : new Date().toISOString(),
        lastUsedAt: uploaded?.lastUsedAt ? String(uploaded.lastUsedAt) : null,
        usageCount: Number(uploaded?.usageCount || 0),
        createdAt: String(uploaded?.createdAt || new Date().toISOString())
      };
      setPlannedMediaItems((prev) => {
        const alreadyExists = prev.some(
          (entry) => entry.type === 'media' && (entry.mediaUrl || entry.imageUrl) === mediaItem.mediaUrl
        );
        if (alreadyExists) {
          return prev;
        }
        const next = [mediaItem, ...prev].slice(0, 60);
        if (unterrichtenMode === 'structure' && selectedUnitId) {
          void persistPlannedMediaItemsForUnit(selectedUnitId, next);
        }
        return next;
      });
      toast.success('Datei hochgeladen und im Mediendepot hinterlegt');
    } catch (error) {
      toast.error('Datei konnte nicht hochgeladen werden');
    } finally {
      setPlannedMediaUploading(false);
    }
  };

  const handlePresentPlannedMediaOnDisplay = async (item: QuickstartPlannedMediaItem) => {
    if (!connectedDisplayPin) {
      toast.error('Bitte zuerst ein Display koppeln');
      return;
    }
    try {
      if (item.type === 'text') {
        await presentQuickstartDisplayText({
          pin: connectedDisplayPin,
          text: String(item.text || ''),
          title: item.title || undefined
        });
      } else {
        await presentQuickstartDisplayMedia({
          pin: connectedDisplayPin,
          assetId: item.assetId || null,
          mediaUrl: String(item.mediaUrl || item.imageUrl || ''),
          mediaType: item.mediaType || 'image',
          mimeType: item.mimeType,
          title: item.title || undefined,
          sourcePageUrl: item.sourcePageUrl || item.mediaUrl || item.imageUrl
        });
      }
      if (item.assetId) {
        setPlannedMediaItems((prev) => {
          const next = prev.map((entry) =>
            entry.id === item.id
              ? {
                  ...entry,
                  lastUsedAt: new Date().toISOString(),
                  usageCount: Number(entry.usageCount || 0) + 1
                }
              : entry
          );
          if (unterrichtenMode === 'structure' && selectedUnitId) {
            void persistPlannedMediaItemsForUnit(selectedUnitId, next);
          }
          return next;
        });
      }
      toast.success('Medium wird auf dem Display angezeigt');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Medium konnte nicht ans Display gesendet werden');
    }
  };

  const scheduleQuickstartStateSave = useCallback(
    (override?: { bookingCounts?: Record<string, number> }) => {
      if (!quickstartTeachingUnitId) {
        return;
      }
      if (quickstartStateSaveTimerRef.current) {
        clearTimeout(quickstartStateSaveTimerRef.current);
      }
      quickstartStateSaveTimerRef.current = setTimeout(async () => {
        try {
          await saveTeachingQuickstartState(quickstartTeachingUnitId, {
            selectedCompetencyIds,
            selectedKeywordHint,
            bookingCounts: override?.bookingCounts ?? quickstartBookingCounts,
            keywordHints: quickstartContext?.keywordHints || [],
            plannedMediaItems
          });
        } catch (error) {
          console.warn('Failed to persist quickstart state:', error);
        }
      }, 350);
    },
    [quickstartTeachingUnitId, selectedCompetencyIds, selectedKeywordHint, quickstartBookingCounts, quickstartContext, plannedMediaItems]
  );

  useEffect(() => {
    if (!quickstartTeachingUnitId) {
      return;
    }
    scheduleQuickstartStateSave();
  }, [quickstartTeachingUnitId, selectedCompetencyIds, selectedKeywordHint, scheduleQuickstartStateSave]);

  useEffect(() => {
    return () => {
      if (quickstartStateSaveTimerRef.current) {
        clearTimeout(quickstartStateSaveTimerRef.current);
        quickstartStateSaveTimerRef.current = null;
      }
      if (statePersistTimerRef.current) {
        clearTimeout(statePersistTimerRef.current);
        statePersistTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!stateRestoreDoneRef.current) {
      return;
    }
    if (statePersistTimerRef.current) {
      clearTimeout(statePersistTimerRef.current);
    }
    statePersistTimerRef.current = setTimeout(() => {
      try {
        const snapshot = {
          savedAt: Date.now(),
          statusFilter,
          listScope,
          unterrichtenMode,
          selectedUnitId,
          parentType,
          selectedParentId,
          selectedClassName,
          title,
          subjectId,
          curriculumId,
          primarySitzplanId,
          nfcAutoEnabled,
          docAutoEnabled,
          startAt,
          endAt,
          contextStudents,
          contextError,
          quickstartContextType,
          quickstartClassName,
          quickstartCourseId,
          quickstartGroupId,
          quickstartSubjectId,
          quickstartTheme,
          quickstartContext,
          quickstartError,
          selectedQuickstartSitzplanId,
          selectedCompetencyIds,
          quickstartTeachingUnitId,
          quickstartTeachingStatus,
          selectedKeywordHint,
          quickstartBookingCounts,
          plannedMediaItems,
          quickstartAwardedCompetencyCounts,
          quickstartNoSitzplanStudents,
          quickstartDisplayToastEnabled,
          quickstartDisplayRankingToastEnabled,
          displayPin,
          connectedDisplayPin,
          showDisplayConnectForm,
          isLeftPanelCollapsed,
          isRightPanelCollapsed
        };
        sessionStorage.setItem(UNTERRICHTEN_SESSION_KEY, JSON.stringify(snapshot));
      } catch (error) {
        console.warn('Failed to persist Unterrichten session state:', error);
      }
    }, 180);
  }, [
    statusFilter,
    listScope,
    unterrichtenMode,
    selectedUnitId,
    parentType,
    selectedParentId,
    selectedClassName,
    title,
    subjectId,
    curriculumId,
    primarySitzplanId,
    nfcAutoEnabled,
    docAutoEnabled,
    startAt,
    endAt,
    contextStudents,
    contextError,
    quickstartContextType,
    quickstartClassName,
    quickstartCourseId,
    quickstartGroupId,
    quickstartSubjectId,
    quickstartTheme,
    quickstartContext,
    quickstartError,
    selectedQuickstartSitzplanId,
    selectedCompetencyIds,
    quickstartTeachingUnitId,
    quickstartTeachingStatus,
    selectedKeywordHint,
    quickstartBookingCounts,
    plannedMediaItems,
    quickstartAwardedCompetencyCounts,
    quickstartNoSitzplanStudents,
    quickstartDisplayToastEnabled,
    quickstartDisplayRankingToastEnabled,
    displayPin,
    connectedDisplayPin,
    showDisplayConnectForm,
    isLeftPanelCollapsed,
    isRightPanelCollapsed
  ]);

  const handleQuickstartStatusChange = (nextStatus: 'paused' | 'completed') => {
    if (!quickstartTeachingUnitId) {
      toast.error('Es gibt keine aktive Einheit');
      return;
    }
    const currentUnitId = quickstartTeachingUnitId;
    updateMutation.mutate(
      {
        id: currentUnitId,
        payload: { status: nextStatus }
      },
      {
        onSuccess: () => {
          setQuickstartTeachingStatus(nextStatus);
          setUnterrichtenMode('structure');
          setSelectedUnitId(currentUnitId);
          if (nextStatus === 'completed') {
            // Nach "Unterricht stoppen" Ad-hoc-Ansicht komplett zurücksetzen.
            setQuickstartClassName('');
            setQuickstartSubjectId('');
            setQuickstartTheme('');
            setQuickstartContext(null);
            setQuickstartError(null);
            setSelectedQuickstartSitzplanId(null);
            setSelectedCompetencyIds([]);
            setSelectedKeywordHint(null);
            setQuickstartTeachingUnitId(null);
            setQuickstartTeachingStatus(null);
            setQuickstartActionLoadingStudentId(null);
            setQuickstartBookingCounts({});
            setPlannedMediaItems([]);
            setQuickstartAwardedCompetencyCounts({});
            setQuickstartNoSitzplanStudents(null);
          }
        }
      }
    );
  };

  const handleQuickstartAddBundle = async (studentId: string) => {
    if (!quickstartTeachingUnitId) {
      toast.error('Bitte zuerst "Unterricht starten" ausführen');
      return;
    }
    if (quickstartTeachingStatus !== 'active') {
      toast.error('Belege können nur bei aktivem Unterricht gebucht werden');
      return;
    }
    if (selectedCompetencyIds.length === 0) {
      toast.error('Keine Kompetenzen ausgewählt');
      return;
    }

    setQuickstartActionLoadingStudentId(studentId);
    try {
      const result = await addQuickstartCompetencyBundle(quickstartTeachingUnitId, {
        studentId,
        competencyIds: selectedCompetencyIds,
        displayToastsEnabled: quickstartDisplayToastEnabled,
        displayRankingEnabled: quickstartDisplayRankingToastEnabled
      });
      setQuickstartBookingCounts((prev) => {
        const next = {
          ...prev,
          [studentId]: (prev[studentId] || 0) + 1
        };
        scheduleQuickstartStateSave({ bookingCounts: next });
        return next;
      });
      setQuickstartAwardedCompetencyCounts((prev) => ({
        ...prev,
        [studentId]: (prev[studentId] || 0) + (result?.createdCount ?? selectedCompetencyIds.length)
      }));
      toast.success(
        `${result?.createdCount ?? selectedCompetencyIds.length} Kompetenzen für Schüler hinzugefügt`
      );
    } catch (error: any) {
      console.error('Error adding quickstart bundle:', error);
      const message =
        error?.response?.data?.error || error?.response?.data?.message || 'Kompetenzen konnten nicht hinzugefügt werden';
      toast.error(message);
    } finally {
      setQuickstartActionLoadingStudentId(null);
    }
  };

  const handleQuickstartRemoveBundle = async (studentId: string) => {
    if (!quickstartTeachingUnitId) {
      toast.error('Bitte zuerst "Unterricht starten" ausführen');
      return;
    }
    if (quickstartTeachingStatus !== 'active') {
      toast.error('Belege können nur bei aktivem Unterricht entzogen werden');
      return;
    }

    setQuickstartActionLoadingStudentId(studentId);
    try {
      const result = await removeQuickstartCompetencyBundle(quickstartTeachingUnitId, {
        studentId,
        displayToastsEnabled: quickstartDisplayToastEnabled,
        displayRankingEnabled: quickstartDisplayRankingToastEnabled
      });
      setQuickstartBookingCounts((prev) => {
        const next = {
          ...prev,
          [studentId]: Math.max(0, (prev[studentId] || 0) - 1)
        };
        scheduleQuickstartStateSave({ bookingCounts: next });
        return next;
      });
      setQuickstartAwardedCompetencyCounts((prev) => ({
        ...prev,
        [studentId]: Math.max(0, (prev[studentId] || 0) - (result?.removedCount ?? 0))
      }));
      toast.success(`${result?.removedCount ?? 0} zuletzt vergebene Kompetenzen entfernt`);
    } catch (error: any) {
      console.error('Error removing quickstart bundle:', error);
      const message =
        error?.response?.data?.error || error?.response?.data?.message || 'Letztes Kompetenz-Bundle nicht gefunden';
      toast.error(message);
    } finally {
      setQuickstartActionLoadingStudentId(null);
    }
  };

  const handleQuickstartEmail = (studentId: string) => {
    const student = quickstartStudentMap.get(studentId);
    if (!student) {
      return;
    }
    setSelectedStudentForEmail(student);
    setShowEmailModal(true);
  };

  const renderQuickstartStudentTile = (student: QuickstartStudentView, key: string) => {
    return (
      <StudentSeatCard
        key={key}
        student={student}
        photoUrl={getPhotoUrl(student.id)}
        compact
        badgeCount={quickstartBookingCounts[student.id] || 0}
        badgeTitle={`Bundles: ${quickstartBookingCounts[student.id] || 0} · Kompetenzen gesamt: ${quickstartAwardedCompetencyCounts[student.id] || 0}`}
        actions={
          <div className="flex items-center gap-1">
            {quickstartTeachingUnitId != null && (
              <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                <AbsenceToggle
                  contextType="teaching_unit"
                  contextId={quickstartTeachingUnitId}
                  studentUnid={student.id}
                  activeAbsence={quickstartActiveAbsencesMap[student.id] ?? null}
                  onStarted={() => refetchQuickstartActiveAbsences()}
                  onEnded={() => refetchQuickstartActiveAbsences()}
                  compact
                  className="rounded p-1"
                />
              </div>
            )}
            <button
              type="button"
              onClick={() => handleQuickstartAddBundle(student.id)}
              disabled={quickstartActionLoadingStudentId === student.id}
              className="rounded bg-green-100 p-1 text-green-700 hover:bg-green-200 dark:bg-green-950/60 dark:text-green-300 dark:hover:bg-green-950"
              title="Kompetenz-Bundle hinzufügen"
            >
              <PlusCircle className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => handleQuickstartRemoveBundle(student.id)}
              disabled={quickstartActionLoadingStudentId === student.id}
              className="rounded bg-amber-100 p-1 text-amber-700 hover:bg-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:hover:bg-amber-950"
              title="Zuletzt vergebenes Bundle entfernen"
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => handleQuickstartEmail(student.id)}
              className="rounded bg-purple-100 p-1 text-purple-700 hover:bg-purple-200 dark:bg-purple-950/60 dark:text-purple-300 dark:hover:bg-purple-950"
              title="E-Mail"
            >
              <Mail className="h-3.5 w-3.5" />
            </button>
          </div>
        }
      />
    );
  };

  const handleCreate = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!title.trim()) {
      toast.error('Bitte einen Titel eingeben');
      return;
    }

    if (parentType !== 'ad_hoc' && !selectedParentId) {
      toast.error('Bitte eine Klasse, einen Kurs oder eine Gruppe auswählen');
      return;
    }

    const payload: TeachingUnitCreatePayload = {
      title: title.trim(),
      parent_type: parentType,
      status: 'draft',
      nfc_auto_enabled: nfcAutoEnabled,
      doc_auto_enabled: docAutoEnabled
    };

    if (parentType === 'class' && selectedParentId && Number(selectedParentId) > 0) {
      payload.parent_id = Number(selectedParentId);
    } else if ((parentType === 'course' || parentType === 'group') && selectedParentId && Number(selectedParentId) > 0) {
      payload.parent_id = Number(selectedParentId);
    }

    if (subjectId) {
      payload.subject_id = Number(subjectId);
    }

    if (curriculumId) {
      payload.curriculum_id = Number(curriculumId);
    }

    if (primarySitzplanId) {
      payload.primary_sitzplan_id = Number(primarySitzplanId);
    }

    if (startAt) {
      payload.start_at = new Date(startAt).toISOString();
    }

    if (endAt) {
      payload.end_at = new Date(endAt).toISOString();
    }

    if (parentType !== 'ad_hoc' && contextStudents.length > 0) {
      payload.participants = contextStudents.map((student) => ({
        student_id: student.id,
        origin: parentType,
        origin_reference:
          parentType === 'class'
            ? selectedClassName
            : (parentType === 'course' || parentType === 'group') && selectedParentId
            ? String(selectedParentId)
            : null,
        is_active: true
      }));
    }

    createMutation.mutate(payload);
  };

  const handleStatusChange = (unitId: number, nextStatus: TeachingUnitSummary['status']) => {
    updateMutation.mutate({
      id: unitId,
      payload: {
        status: nextStatus
      }
    });
  };

  const handleMoveToTrash = (unit: TeachingUnitSummary) => {
    const confirmed = window.confirm(`Unterrichtseinheit "${unit.title}" in den Papierkorb verschieben?`);
    if (!confirmed) {
      return;
    }
    moveTeachingUnitToTrash(unit.id)
      .then(() => {
        toast.success('In den Papierkorb verschoben');
        queryClient.invalidateQueries(['unterrichten', 'units']);
      })
      .catch((error: any) => {
        const message = error?.response?.data?.error || 'Verschieben in Papierkorb fehlgeschlagen';
        toast.error(message);
      });
  };

  const handleRestoreFromTrash = (unit: TeachingUnitSummary) => {
    restoreTeachingUnitFromTrash(unit.id)
      .then(() => {
        toast.success('Aus Papierkorb wiederhergestellt');
        queryClient.invalidateQueries(['unterrichten', 'units']);
      })
      .catch((error: any) => {
        const message = error?.response?.data?.error || 'Wiederherstellen fehlgeschlagen';
        toast.error(message);
      });
  };

  const handleSeriesChange = (unitId: number, value: string) => {
    let nextSeries = value;
    if (value === '__new__') {
      const created = window.prompt('Name der Reihe eingeben:');
      nextSeries = created?.trim() || '';
    }
    updateMutation.mutate({
      id: unitId,
      payload: {
        series_name: nextSeries || null
      }
    });
  };

  const handleSwitchToTileView = async (unit: TeachingUnitSummary) => {
    setSwitchingUnitId(unit.id);
    setQuickstartLoading(true);
    setQuickstartError(null);
    try {
      const detail = await fetchTeachingUnitDetail(unit.id);
      const participantClasses = Array.from(
        new Set((detail?.participants || []).map((participant) => participant.klasse).filter(Boolean))
      ) as string[];
      if (unit.parent_type !== 'class' && participantClasses.length > 1) {
        toast.error('Kachelansicht aktuell nur für Einheiten mit eindeutiger Klasse verfügbar');
        return;
      }
      const className =
        unit.parent_type === 'class'
          ? unit.context_label || participantClasses[0] || ''
          : participantClasses[0] || '';

      if (!className) {
        toast.error('Für diese Einheit konnte keine Klasse für die Kachelansicht ermittelt werden');
        return;
      }

      const context = await fetchTeachingQuickstartContext({
        className: String(className),
        subjectId: unit.subject_id || undefined,
        theme: unit.title
      });
      const summaryRows = await fetchTeachingQuickstartStudentCompetencySummary(unit.id);
      let savedState = null;
      try {
        savedState = await fetchTeachingQuickstartState(unit.id);
      } catch (error) {
        // Keep tile view usable even if state persistence table is not migrated yet.
        console.warn('Quickstart state could not be loaded:', error);
      }

      setUnterrichtenMode('ad_hoc');
      setQuickstartContextType(
        unit.parent_type === 'course' ? 'course' : unit.parent_type === 'group' ? 'group' : 'class'
      );
      setQuickstartClassName(String(className));
      setQuickstartCourseId(unit.parent_type === 'course' && unit.parent_id ? Number(unit.parent_id) : '');
      setQuickstartGroupId(unit.parent_type === 'group' && unit.parent_id ? Number(unit.parent_id) : '');
      setQuickstartSubjectId(unit.subject_id || '');
      setQuickstartTheme(unit.title || '');
      const restoredKeywordHints =
        savedState?.keywordHints && savedState.keywordHints.length > 0 ? savedState.keywordHints : context.keywordHints;
      setQuickstartContext({
        ...context,
        keywordHints: restoredKeywordHints
      });
      const participantsForFallback = (detail?.participants || [])
        .map((participant) => ({
          id: String(participant.sophomorix_unid || participant.student_id || ''),
          firstName: participant.first_name || '',
          lastName: participant.last_name || '',
          className: participant.klasse || className
        }))
        .filter((student) => student.id);
      setQuickstartNoSitzplanStudents(participantsForFallback.length > 0 ? participantsForFallback : null);
      setSelectedQuickstartSitzplanId(unit.primary_sitzplan_id || context.suggestedSitzplanId || null);

      const competencyIdsFromUnit = (detail?.competencies || [])
        .map((competency) => Number(competency.competency_id))
        .filter(Boolean);
      setSelectedCompetencyIds(
        savedState?.selectedCompetencyIds?.length
          ? savedState.selectedCompetencyIds
          : competencyIdsFromUnit.length
          ? competencyIdsFromUnit
          : context.defaultSelectedCompetencyIds?.length
          ? context.defaultSelectedCompetencyIds
          : context.competencySuggestions.map((competency) => competency.id)
      );

      setSelectedKeywordHint(
        savedState?.selectedKeywordHint ||
          (Array.isArray(restoredKeywordHints) && restoredKeywordHints.length > 0 ? restoredKeywordHints[0] : null)
      );
      setPlannedMediaItems(Array.isArray(savedState?.plannedMediaItems) ? savedState.plannedMediaItems : []);
      setQuickstartTeachingUnitId(unit.id);
      setQuickstartTeachingStatus(unit.status);
      const summaryBundleCounts = summaryRows.reduce<Record<string, number>>((acc, row) => {
        acc[row.studentId] = row.quickstartBundleCount;
        return acc;
      }, {});
      const summaryCompetencyCounts = summaryRows.reduce<Record<string, number>>((acc, row) => {
        acc[row.studentId] = row.totalEvidenceCount;
        return acc;
      }, {});
      setQuickstartBookingCounts(
        Object.keys(summaryBundleCounts).length > 0 ? summaryBundleCounts : savedState?.bookingCounts || {}
      );
      setQuickstartAwardedCompetencyCounts(summaryCompetencyCounts);
      toast.success('Einheit in Kachelansicht geöffnet');
    } catch (error: any) {
      console.error('Error switching unit to tile view:', error);
      const message =
        error?.response?.data?.error || error?.response?.data?.message || 'Kachelansicht konnte nicht geöffnet werden';
      setQuickstartError(message);
      toast.error(message);
    } finally {
      setSwitchingUnitId(null);
      setQuickstartLoading(false);
    }
  };

  const renderUnitRow = (unit: TeachingUnitSummary) => {
    const titleParts = String(unit.title || '')
      .split(' – ')
      .map((part) => part.trim())
      .filter(Boolean);
    const parsedTheme = titleParts.length >= 3 ? titleParts.slice(2).join(' – ') : unit.title;
    const parsedClassFromTitle = titleParts.length >= 2 ? titleParts[1] : '';
    const parsedSubjectFromTitle = titleParts.length >= 2 ? titleParts[0] : '';

    const contextLabel = (() => {
      const raw = String(unit.context_label || '').trim();
      const parentId = unit.parent_id !== null && unit.parent_id !== undefined ? String(unit.parent_id) : '';
      if (unit.parent_type === 'class') {
        if (raw && !/^\d+$/.test(raw)) {
          return raw;
        }
        return classLabelByKey.get(raw) || classLabelByKey.get(parentId) || parsedClassFromTitle || '–';
      }
      if (unit.parent_type === 'course') {
        return courseLabelById.get(raw) || courseLabelById.get(parentId) || (raw && !/^\d+$/.test(raw) ? raw : '–');
      }
      if (unit.parent_type === 'group') {
        return groupLabelById.get(raw) || groupLabelById.get(parentId) || (raw && !/^\d+$/.test(raw) ? raw : '–');
      }
      return raw || 'Ad-Hoc';
    })();

    const subjectLabel = unit.subject_id
      ? subjectLabelById.get(Number(unit.subject_id)) || parsedSubjectFromTitle || '–'
      : parsedSubjectFromTitle || '–';

    return (
      <tr
        key={unit.id}
        className="border-b border-gray-100 hover:bg-gray-50 transition cursor-pointer"
        onClick={() => setSelectedUnitId(unit.id)}
      >
        <td className="px-4 py-3">
          <div className="font-semibold text-gray-900">{parsedTheme}</div>
          <div className="text-xs text-gray-500">{new Date(unit.created_at).toLocaleDateString()}</div>
        </td>
        <td className="px-4 py-3 text-sm text-gray-600">{contextLabel}</td>
        <td className="px-4 py-3 text-sm text-gray-600">{subjectLabel}</td>
        <td className="px-4 py-3">
          <span className="inline-flex items-center rounded-full bg-primary-50 px-2 py-1 text-xs font-medium text-primary-600">
            {unit.status}
          </span>
        </td>
        <td className="px-4 py-3">
          <select
            value={unit.series_name || ''}
            onChange={(event) => {
              event.stopPropagation();
              handleSeriesChange(unit.id, event.target.value);
            }}
            className="w-full max-w-[12rem] rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700"
          >
            <option value="">Ohne Reihe</option>
            {seriesOptions.map((series) => (
              <option key={series} value={series}>
                {series}
              </option>
            ))}
            <option value="__new__">+ Neue Reihe…</option>
          </select>
        </td>
        <td className="px-4 py-3 text-sm text-gray-600">
          {unit.start_at ? format(parseISO(unit.start_at), 'dd.MM.yyyy HH:mm') : '–'}
        </td>
        <td className="px-4 py-3 text-right">
          <div className="inline-flex flex-wrap items-center justify-end gap-2">
            {listScope === 'trash' ? (
              <button
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 py-1 text-sm text-gray-600 hover:bg-gray-100"
                onClick={(event) => {
                  event.stopPropagation();
                  handleRestoreFromTrash(unit);
                }}
              >
                <RotateCcw className="h-4 w-4" />
                Wiederherstellen
              </button>
            ) : null}
            <button
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={(event) => {
                event.stopPropagation();
                handleSwitchToTileView(unit);
              }}
              disabled={switchingUnitId === unit.id}
            >
              {switchingUnitId === unit.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />}
              Kachelansicht
            </button>
            {listScope !== 'trash' ? (
              <button
                className="inline-flex items-center gap-1 rounded-md border border-red-200 px-3 py-1 text-sm text-red-600 hover:bg-red-50"
                onClick={(event) => {
                  event.stopPropagation();
                  handleMoveToTrash(unit);
                }}
              >
                <Trash2 className="h-4 w-4" />
                Löschen
              </button>
            ) : null}
            <button
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 py-1 text-sm text-gray-600 hover:bg-gray-100"
              onClick={(event) => {
                event.stopPropagation();
                const statusIndex = statusOptions.indexOf(unit.status);
                const nextStatus = statusOptions[(statusIndex + 1) % statusOptions.length];
                handleStatusChange(unit.id, nextStatus);
              }}
            >
              Status wechseln
            </button>
            <a
              className="inline-flex items-center gap-1 rounded-md border border-blue-200 px-3 py-1 text-sm text-blue-700 hover:bg-blue-50"
              href={`/klassenbuch?unitType=${encodeURIComponent(String(unit.parent_type || ''))}&unitId=${encodeURIComponent(String(unit.parent_id || ''))}&tuId=${unit.id}`}
              onClick={(event) => event.stopPropagation()}
              title="Im Klassenbuch öffnen"
            >
              <BookOpen className="h-4 w-4" />
              Klassenbuch
            </a>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-6">
      {/* Stichworte-Modal */}
      {showKeywordsModal && quickstartContext && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setShowKeywordsModal(false)}
        >
          <div
            className="relative flex w-full max-w-2xl flex-col rounded-xl bg-white shadow-2xl"
            style={{ maxHeight: '90vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-6 py-4">
              <div className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-amber-500" />
                <h2 className="text-base font-semibold text-gray-800">Stichworte zum Thema</h2>
              </div>
              <button
                type="button"
                onClick={() => setShowKeywordsModal(false)}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-6">
              {/* Trefferbasis */}
              {((quickstartContext.curriculumMatches?.length || 0) > 0 ||
                (quickstartContext.contentFieldMatches?.length || 0) > 0) && (
                <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
                  <p className="text-xs font-semibold text-gray-600">Trefferbasis</p>
                  {(quickstartContext.curriculumMatches || []).length > 0 && (
                    <p className="mt-1 text-xs text-gray-500">
                      Curriculum:{' '}
                      {(quickstartContext.curriculumMatches || []).slice(0, 2).map((m) => m.name).join(' · ')}
                    </p>
                  )}
                  {(quickstartContext.contentFieldMatches || []).length > 0 && (
                    <p className="mt-1 text-xs text-gray-500">
                      Inhaltsfelder:{' '}
                      {(quickstartContext.contentFieldMatches || []).slice(0, 4).map((m) => m.name).join(' · ')}
                    </p>
                  )}
                </div>
              )}

              {/* Keyword-Grid */}
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {(quickstartContext.keywordHints || []).slice(0, 10).map((hint, index) => (
                  <button
                    key={`modal-kw-${hint.keyword}-${index}`}
                    type="button"
                    onClick={() => {
                      setSelectedKeywordHint(hint);
                      setKeywordDetailText(hint.explanation || '');
                    }}
                    className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                      selectedKeywordHint?.keyword === hint.keyword
                        ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                        : 'border-gray-200 bg-white text-gray-700 hover:bg-amber-50 hover:border-amber-300'
                    }`}
                  >
                    {hint.keyword}
                  </button>
                ))}
              </div>

              {/* Detail-Bereich */}
              {selectedKeywordHint && (
                <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="text-sm font-semibold text-gray-800">{selectedKeywordHint.keyword}</p>
                  <textarea
                    value={keywordDetailText}
                    onChange={(e) => setKeywordDetailText(e.target.value)}
                    rows={5}
                    className="w-full resize-none rounded-md border border-gray-200 bg-white px-3 py-2 text-sm leading-relaxed text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={keywordDetailLoading}
                      onClick={async () => {
                        setKeywordDetailLoading(true);
                        try {
                          const curriculum = (quickstartContext.curriculumMatches || [])
                            .slice(0, 2).map((m) => m.name).join(', ');
                          const text = await fetchKeywordDetails({
                            keyword: selectedKeywordHint.keyword,
                            theme: quickstartContext.theme,
                            className: quickstartContext.className,
                            curriculum: curriculum || undefined
                          });
                          setKeywordDetailText(text);
                        } catch {
                          toast.error('Mehr Details konnten nicht geladen werden');
                        } finally {
                          setKeywordDetailLoading(false);
                        }
                      }}
                      className="flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                    >
                      {keywordDetailLoading ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Lightbulb className="h-3.5 w-3.5" />
                      )}
                      Mehr Details
                    </button>
                    <button
                      type="button"
                      disabled={!keywordDetailText.trim()}
                      onClick={() => {
                        if (!selectedKeywordHint || !keywordDetailText.trim()) {
                          return;
                        }
                        handleAddTextToPlannedMedia({
                          title: selectedKeywordHint.keyword,
                          text: keywordDetailText.trim()
                        });
                      }}
                      className="flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                    >
                      + Depot
                    </button>
                    {connectedDisplayPin && (
                      <button
                        type="button"
                        disabled={keywordDisplaySending || !keywordDetailText.trim()}
                        onClick={async () => {
                          setKeywordDisplaySending(true);
                          try {
                            await presentQuickstartDisplayText({
                              pin: connectedDisplayPin,
                              text: keywordDetailText.trim(),
                              title: selectedKeywordHint.keyword
                            });
                            toast.success('Text wird auf dem Display angezeigt');
                          } catch (error: any) {
                            toast.error(error?.response?.data?.error || 'Konnte nicht ans Display gesendet werden');
                          } finally {
                            setKeywordDisplaySending(false);
                          }
                        }}
                        className="flex items-center gap-1.5 rounded-md border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                      >
                        {keywordDisplaySending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Monitor className="h-3.5 w-3.5" />
                        )}
                        Aufs Display
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bildideen-Modal */}
      {showImagesModal && (quickstartContext || unterrichtenMode === 'structure') && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setShowImagesModal(false)}
        >
          <div
            className="relative w-full max-w-lg rounded-xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <div className="flex items-center gap-2">
                <Image className="h-4 w-4 text-blue-500" />
                <h2 className="text-sm font-semibold text-gray-800">Bildideen</h2>
              </div>
              <button
                type="button"
                onClick={() => setShowImagesModal(false)}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5">
              <form
                className="flex gap-2"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const q = imageSearchInput.trim();
                  if (!q) return;
                  setImageSearchLoading(true);
                  setManualImageGallery([]);
                  try {
                    const groups = await fetchImageSearch(q, quickstartContext?.theme || quickstartTheme || title);
                    setManualImageGallery(groups);
                  } catch {
                    toast.error('Bildersuche fehlgeschlagen');
                  } finally {
                    setImageSearchLoading(false);
                  }
                }}
              >
                <input
                  type="text"
                  value={imageSearchInput}
                  onChange={(e) => setImageSearchInput(e.target.value)}
                  placeholder="Suchbegriff…"
                  autoFocus
                  className="min-w-0 flex-1 rounded border border-gray-200 px-3 py-1.5 text-sm text-gray-800 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                <button
                  type="submit"
                  disabled={imageSearchLoading || !imageSearchInput.trim()}
                  className="rounded bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {imageSearchLoading ? '…' : 'Suchen'}
                </button>
              </form>
              {(() => {
                const gallery = manualImageGallery.length > 0
                  ? manualImageGallery
                  : (quickstartContext?.imageGallery || []);
                return (
                  <div className="mt-4 max-h-[60vh] space-y-4 overflow-y-auto overflow-x-visible pr-1">
                    {gallery.length === 0 ? (
                      <p className="text-sm text-gray-500">
                        Suchbegriff eingeben und auf Suchen klicken.
                      </p>
                    ) : (
                      gallery.slice(0, 6).map((group, groupIndex) => (
                        <div key={`modal-img-group-${group.query}-${groupIndex}`} className="space-y-1.5">
                          <p className="text-xs font-semibold text-gray-700">{group.query}</p>
                          <div className="grid grid-cols-3 gap-2 overflow-visible">
                            {(group.images || []).slice(0, 6).map((image, imageIndex) => (
                              <div
                                key={`modal-img-${groupIndex}-${imageIndex}-${image.imageUrl}`}
                                className="group relative overflow-visible rounded border border-gray-200 bg-gray-100"
                              >
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleAddImageToPlannedMedia({
                                      mediaUrl: image.imageUrl,
                                      title: image.title || group.query,
                                      sourcePageUrl: image.sourcePageUrl || image.imageUrl
                                    });
                                  }}
                                  className="absolute left-0.5 top-0.5 z-20 rounded bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white hover:bg-black/75"
                                  title="Im Mediendepot hinterlegen"
                                >
                                  +Depot
                                </button>
                                <button
                                  type="button"
                                  title={
                                    connectedDisplayPin
                                      ? `Bild auf Display zeigen: ${image.title || group.query}`
                                      : (image.title || group.query)
                                  }
                                  onClick={async () => {
                                    if (!connectedDisplayPin) return;
                                    try {
                                      await presentQuickstartDisplayImage({
                                        pin: connectedDisplayPin,
                                        imageUrl: image.imageUrl,
                                        title: image.title || group.query,
                                        sourcePageUrl: image.sourcePageUrl || image.imageUrl
                                      });
                                      toast.success('Bild wird auf dem Display angezeigt');
                                    } catch (err: any) {
                                      toast.error(err?.response?.data?.error || 'Konnte nicht ans Display gesendet werden');
                                    }
                                  }}
                                  className={`block w-full ${connectedDisplayPin ? 'cursor-pointer' : 'cursor-default'}`}
                                >
                                  <img
                                    src={image.thumbnailUrl || image.imageUrl}
                                    alt={image.title || group.query}
                                    className="h-20 w-full rounded object-cover transition-transform duration-200 group-hover:scale-[2] group-hover:relative group-hover:z-50 group-hover:shadow-xl"
                                    loading="lazy"
                                  />
                                </button>
                                <a
                                  href={image.sourcePageUrl || image.imageUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="absolute bottom-0.5 right-0.5 z-10 hidden rounded bg-black/50 p-0.5 text-white group-hover:flex"
                                  title="In neuem Tab öffnen"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                    <polyline points="15 3 21 3 21 9" />
                                    <line x1="10" y1="14" x2="21" y2="3" />
                                  </svg>
                                </a>
                              </div>
                            ))}
                          </div>
                          <p className="text-[10px] text-gray-400">
                            {(group.images || []).slice(0, 1)
                              .map((img) => [img.provider, img.license || img.author].filter(Boolean).join(' · '))
                              .join('')}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Unterrichten</h1>
          <p className="mt-1 text-sm text-gray-500">
            Unterrichtseinheiten vorbereiten, Kompetenzen dokumentieren und Anwesenheit verwalten.
          </p>
          {unterrichtenMode === 'structure' && (
            <div className="mt-2 inline-flex items-center rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
              Ansicht: {listScopeLabel}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Link
            to={
              selectedUnitId
                ? `/unterrichten/klassenarbeit?teachingUnitId=${selectedUnitId}`
                : '/unterrichten/klassenarbeit'
            }
            className="inline-flex items-center rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-xs font-semibold text-primary-700 hover:bg-primary-100"
          >
            Klassenarbeit öffnen
          </Link>
          <div className="inline-flex rounded-md border border-gray-200 bg-white p-1 shadow-sm">
            <button
              type="button"
              onClick={() => setUnterrichtenMode('ad_hoc')}
              className={`rounded px-3 py-1.5 text-xs font-medium ${
                unterrichtenMode === 'ad_hoc' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Schnellstart (ad hoc)
            </button>
            <button
              type="button"
              onClick={() => setUnterrichtenMode('structure')}
              className={`rounded px-3 py-1.5 text-xs font-medium ${
                unterrichtenMode === 'structure' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Mein Unterricht
            </button>
          </div>
          {unterrichtenMode === 'structure' && (
            <>
              <div className="inline-flex overflow-hidden rounded-md border border-gray-200">
                <button
                  className={`inline-flex items-center gap-2 px-3 py-2 text-sm ${
                    listScope === 'normal' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  onClick={() => {
                    setUnterrichtenMode('structure');
                    setListScope('normal');
                    if (statusFilter === 'archived') {
                      setStatusFilter('all');
                    }
                  }}
                >
                  Normal
                </button>
                <button
                  className={`inline-flex items-center gap-2 border-l border-gray-200 px-3 py-2 text-sm ${
                    listScope === 'archive' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  onClick={() => {
                    setUnterrichtenMode('structure');
                    setListScope('archive');
                  }}
                >
                  Archiv
                </button>
                <button
                  className={`inline-flex items-center gap-2 border-l border-gray-200 px-3 py-2 text-sm ${
                    listScope === 'trash' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  onClick={() => {
                    setUnterrichtenMode('structure');
                    setListScope('trash');
                  }}
                  title="Papierkorb anzeigen"
                >
                  <Trash2 className="h-4 w-4" />
                  Papierkorb
                </button>
              </div>
              <button
                className="inline-flex items-center gap-2 rounded-md border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
                onClick={() => queryClient.invalidateQueries(['unterrichten', 'units'])}
              >
                <RefreshCw className="h-4 w-4" />
                Aktualisieren
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <section
          className={`min-w-0 rounded-lg border border-gray-200 bg-white shadow-sm ${
            unterrichtenMode === 'ad_hoc' ? 'hidden' : 'lg:col-span-2'
          }`}
        >
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h2 className="text-lg font-medium text-gray-900">Vorhandene Unterrichtseinheiten</h2>
            <div className="flex items-center gap-3">
              <select
                value={listScope === 'archive' ? 'archived' : statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}
                disabled={listScope !== 'normal'}
                className="rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
              >
                <option value="all">Alle Status</option>
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="relative">
            {unitsFetching && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
              </div>
            )}

            <div className="w-full overflow-x-auto">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      Einheit
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      Klasse/Kurs/Gruppe
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      Fach
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      Status
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      Reihe
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      Start
                    </th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {unitsLoading ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                        <Loader2 className="mx-auto h-5 w-5 animate-spin text-primary-500" />
                      </td>
                    </tr>
                  ) : groupedTeachingUnits.length > 0 ? (
                    groupedTeachingUnits.flatMap(([seriesName, units]) => [
                      <tr key={`series-${seriesName}`} className="bg-gray-50">
                        <td colSpan={7} className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
                          Reihe: {seriesName}
                        </td>
                      </tr>,
                      ...units.map(renderUnitRow)
                    ])
                  ) : (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                        Noch keine Unterrichtseinheiten vorhanden.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section
          className={`rounded-lg border border-gray-200 bg-white shadow-sm ${
            unterrichtenMode === 'ad_hoc' ? 'lg:col-span-3' : ''
          }`}
        >
          <div className="border-b border-gray-200 px-4 py-3">
            <h2 className="text-lg font-medium text-gray-900">
              {isQuickstartSessionActive ? 'Ad-hoc Unterricht läuft' : 'Neue Einheit vorbereiten'}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Schnellstart für ad-hoc Unterricht oder Einstieg über die bisherige Struktur.
            </p>
          </div>
          {unterrichtenMode === 'ad_hoc' && (
            <div className="space-y-4 px-4 py-4">
              {!isQuickstartSessionActive && (
                <>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Kontext</label>
                      <div className="mt-1 inline-flex w-full rounded-md border border-gray-200 bg-white p-1">
                        {(['class', 'course', 'group'] as const).map((type) => (
                          <button
                            key={type}
                            type="button"
                            onClick={() => {
                              setQuickstartContextType(type);
                              setQuickstartContext(null);
                              setQuickstartTeachingUnitId(null);
                              setQuickstartTeachingStatus(null);
                              setQuickstartNoSitzplanStudents(null);
                            }}
                            className={`flex-1 rounded px-2 py-1.5 text-xs font-medium ${
                              quickstartContextType === type
                                ? 'bg-primary-600 text-white'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            {type === 'class' ? 'Klasse' : type === 'course' ? 'Kurs' : 'Gruppe'}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        {quickstartContextType === 'class'
                          ? 'Klasse'
                          : quickstartContextType === 'course'
                          ? 'Kurs'
                          : 'Gruppe'}
                      </label>
                      <select
                        className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        value={
                          quickstartContextType === 'class'
                            ? quickstartClassName
                            : quickstartContextType === 'course'
                            ? quickstartCourseId
                            : quickstartGroupId
                        }
                        onChange={(event) => {
                          if (quickstartContextType === 'class') {
                            setQuickstartClassName(event.target.value);
                          } else if (quickstartContextType === 'course') {
                            setQuickstartCourseId(event.target.value ? Number(event.target.value) : '');
                          } else {
                            setQuickstartGroupId(event.target.value ? Number(event.target.value) : '');
                          }
                          setQuickstartContext(null);
                          setQuickstartTeachingUnitId(null);
                          setQuickstartTeachingStatus(null);
                          setQuickstartNoSitzplanStudents(null);
                        }}
                      >
                        <option value="">auswählen…</option>
                        {quickstartContextType === 'class' &&
                          classOptions.map((option: any) => (
                            <option key={option.value} value={option.label}>
                              {option.label}
                            </option>
                          ))}
                        {quickstartContextType === 'course' &&
                          courseOptions.map((option: any) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        {quickstartContextType === 'group' &&
                          groupOptions.map((option: any) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Fach</label>
                    <select
                      className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      value={quickstartSubjectId}
                      onChange={(event) => {
                        setQuickstartSubjectId(event.target.value ? Number(event.target.value) : '');
                        setQuickstartContext(null);
                        setQuickstartTeachingUnitId(null);
                        setQuickstartTeachingStatus(null);
                        setQuickstartNoSitzplanStudents(null);
                      }}
                    >
                      <option value="">auswählen…</option>
                      {subjectsFromMeta.map((subject: any) => (
                        <option key={subject.id} value={subject.id}>
                          {subject.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Thema</label>
                    <input
                      value={quickstartTheme}
                      onChange={(event) => {
                        setQuickstartTheme(event.target.value);
                        setQuickstartContext(null);
                        setQuickstartTeachingUnitId(null);
                        setQuickstartTeachingStatus(null);
                        setQuickstartNoSitzplanStudents(null);
                      }}
                      className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      placeholder="z. B. Bruchrechnung addieren"
                    />
                  </div>
                </>
              )}

              {isQuickstartSessionActive ? (
                <div className="flex items-center justify-between gap-3 rounded-md border border-green-200 bg-green-50 px-3 py-2">
                  <div className="truncate text-xs text-green-800">
                    Aktive Einheit: <span className="font-semibold">{quickstartTheme || 'Ohne Thema'}</span> · Kontext:{' '}
                    {quickstartContextLabel || '–'}
                  </div>
                  <div className="inline-flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleQuickstartStart}
                      disabled={quickstartLoading || updateMutation.isLoading}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-blue-300 bg-white text-blue-700 shadow-sm hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
                      title="Unterricht starten/fortsetzen"
                    >
                      {quickstartLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleQuickstartStatusChange('paused')}
                      disabled={!quickstartTeachingUnitId || quickstartTeachingStatus !== 'active' || updateMutation.isLoading}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-amber-300 bg-white text-amber-700 shadow-sm hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-50"
                      title="Unterricht pausieren"
                    >
                      <Pause className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleQuickstartStatusChange('completed')}
                      disabled={!quickstartTeachingUnitId || quickstartTeachingStatus === 'completed' || updateMutation.isLoading}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-red-300 bg-white text-red-700 shadow-sm hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                      title="Unterricht stoppen"
                    >
                      <Square className="h-4 w-4" />
                    </button>
                    <span className="mx-0.5 h-5 w-px bg-green-200" aria-hidden="true" />
                    <button
                      type="button"
                      onClick={() => setShowDisplayConnectForm((prev) => !prev)}
                      className={`inline-flex h-9 w-9 items-center justify-center rounded-full border bg-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50 ${
                        connectedDisplayPin
                          ? 'border-sky-300 text-sky-700 hover:bg-sky-50'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                      title={connectedDisplayPin ? 'Display-Einstellungen' : 'Display koppeln'}
                    >
                      <Monitor className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={handleDisconnectDisplay}
                      disabled={!connectedDisplayPin || displayDisconnectLoading}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-red-300 bg-white text-red-700 shadow-sm hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                      title="Display entkoppeln"
                    >
                      {displayDisconnectLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unplug className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid gap-2 sm:grid-cols-3">
                  <button
                    type="button"
                    onClick={handleQuickstartStart}
                    disabled={quickstartLoading || updateMutation.isLoading}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-primary-300"
                  >
                    {quickstartLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                    Unterricht starten
                  </button>
                  <button
                    type="button"
                    onClick={() => handleQuickstartStatusChange('paused')}
                    disabled={!quickstartTeachingUnitId || quickstartTeachingStatus !== 'active' || updateMutation.isLoading}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Pause className="h-4 w-4" />
                    Unterricht pausieren
                  </button>
                  <button
                    type="button"
                    onClick={() => handleQuickstartStatusChange('completed')}
                    disabled={!quickstartTeachingUnitId || quickstartTeachingStatus === 'completed' || updateMutation.isLoading}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-red-300 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Square className="h-4 w-4" />
                    Unterricht stoppen
                  </button>
                </div>
              )}

              {(showDisplayConnectForm || !connectedDisplayPin) && (
                <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2">
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <input
                      value={displayPin}
                      onChange={(event) => setDisplayPin(event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
                      className="w-full rounded-md border border-sky-300 px-3 py-2 font-mono text-sm tracking-[0.2em] text-gray-900 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                      placeholder="Display-PIN"
                    />
                    <button
                      type="button"
                      onClick={handleConnectDisplay}
                      disabled={displayConnectLoading}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-sky-300 bg-white text-sky-700 hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-50"
                      title={connectedDisplayPin ? 'Display neu koppeln' : 'Display koppeln'}
                    >
                      {displayConnectLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Monitor className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              )}

              {quickstartError && <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-600">{quickstartError}</div>}

              {quickstartContext && (
                <div ref={quickstartWorkspaceAnchorRef} className="space-y-3">
                  <div
                    className="grid min-h-[320px] gap-3"
                    style={{
                      gridTemplateColumns: quickstartLayoutColumns,
                      height: quickstartWorkspaceHeight ?? undefined
                    }}
                  >
                    {!isLeftPanelCollapsed ? (
                      <aside className="flex min-h-[320px] flex-col self-start rounded-md border border-gray-200 bg-gray-50 p-3 lg:sticky lg:top-20" style={{ height: quickstartWorkspaceHeight ?? undefined }}>
                        <div className="mb-2 flex items-center justify-between">
                          <p className="text-sm font-semibold text-gray-800">Thema & Kompetenzen</p>
                          <button
                            type="button"
                            onClick={() => setIsLeftPanelCollapsed(true)}
                            className="rounded p-1 text-gray-500 hover:bg-gray-200"
                            title="Linke Spalte einklappen"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </button>
                        </div>
                        <p className="text-xs text-gray-600">
                          {quickstartTheme} · {quickstartContextLabel || 'ohne Kontext'}
                        </p>
                        <label className="mt-2 text-xs font-medium text-gray-700">Sitzordnung</label>
                        <select
                          className="w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700"
                          value={selectedQuickstartSitzplanId || ''}
                          onChange={(event) =>
                            setSelectedQuickstartSitzplanId(event.target.value ? Number(event.target.value) : null)
                          }
                        >
                          <option value="">Sitzordnung wählen…</option>
                          {quickstartContext.sitzplanSuggestions.map((plan) => (
                            <option key={plan.id} value={plan.id}>
                              {plan.name} ({Math.round(plan.coverage * 100)}%)
                            </option>
                          ))}
                        </select>

                        <div className="mt-3 flex items-center justify-between text-xs text-gray-600">
                          <span>Kompetenzen (Multiselect)</span>
                          <span>{selectedCompetencyIds.length} aktiv</span>
                        </div>
                        <select
                          multiple
                          size={22}
                          value={selectedCompetencyIds.map(String)}
                          onChange={(event) => {
                            const values = Array.from(event.target.selectedOptions).map((option) => Number(option.value));
                            setSelectedCompetencyIds(values);
                          }}
                          className="mt-1 w-full flex-1 rounded-md border border-gray-200 bg-white p-2 text-xs text-gray-700"
                        >
                          {quickstartContext.allCurriculumCompetencies.map((competency) => (
                            <option key={competency.id} value={competency.id}>
                              {competency.name}
                            </option>
                          ))}
                        </select>

                        <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 px-2 py-1.5 text-[11px] text-blue-700">
                          Kompetenzen können hier angepasst werden und wirken direkt für die aktive Einheit.
                        </div>
                        {quickstartTeachingUnitId && (
                          <div
                            className={`mt-2 rounded-md px-2 py-1.5 text-[11px] ${
                              quickstartTeachingStatus === 'paused'
                                ? 'border border-amber-200 bg-amber-50 text-amber-800'
                                : quickstartTeachingStatus === 'completed'
                                ? 'border border-gray-300 bg-gray-100 text-gray-700'
                                : 'border border-green-200 bg-green-50 text-green-700'
                            }`}
                          >
                            {quickstartTeachingStatus === 'paused'
                              ? `Pausiert: Einheit #${quickstartTeachingUnitId}`
                              : quickstartTeachingStatus === 'completed'
                              ? `Gestoppt: Einheit #${quickstartTeachingUnitId}`
                              : `Aktiv: Einheit #${quickstartTeachingUnitId}`}
                          </div>
                        )}
                      </aside>
                    ) : (
                      <aside className="flex min-h-[320px] self-start items-start justify-center rounded-md border border-gray-200 bg-gray-50 p-1 lg:sticky lg:top-20" style={{ height: quickstartWorkspaceHeight ?? undefined }}>
                        <button
                          type="button"
                          onClick={() => setIsLeftPanelCollapsed(false)}
                          className="rounded p-1 text-gray-500 hover:bg-gray-200"
                          title="Linke Spalte ausklappen"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </aside>
                    )}

                    <section
                      className="flex min-h-[320px] flex-col overflow-hidden rounded-md border border-gray-200 bg-white"
                      style={{ height: quickstartWorkspaceHeight ?? undefined }}
                    >
                      {/* View Toggle */}
                      <div className="flex shrink-0 items-center gap-2 border-b border-gray-100 px-3 py-1.5">
                        <div className="inline-flex rounded border border-gray-200 bg-gray-50 p-0.5">
                          <button
                            type="button"
                            onClick={() => setQuickstartCenterView('students')}
                            className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                              quickstartCenterView === 'students'
                                ? 'bg-white text-gray-800 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            <Users className="h-3.5 w-3.5" />
                            Schüler
                          </button>
                          <button
                            type="button"
                            onClick={() => setQuickstartCenterView('display')}
                            className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                              quickstartCenterView === 'display'
                                ? 'bg-white text-gray-800 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            <Monitor className="h-3.5 w-3.5" />
                            Tafel
                            {connectedDisplayPin && (
                              <span className="inline-block h-1.5 w-1.5 rounded-full bg-sky-400" />
                            )}
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => setQuickstartDisplayToastEnabled((prev) => !prev)}
                          className={`ml-auto inline-flex items-center gap-1.5 rounded border px-2.5 py-1 text-[11px] font-medium ${
                            quickstartDisplayToastEnabled
                              ? 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                              : 'border-gray-300 bg-gray-50 text-gray-600 hover:bg-gray-100'
                          }`}
                          title="Motivations-Toast auf gekoppelten Display bei Kompetenz +1/-1"
                        >
                          Display-Feedback: {quickstartDisplayToastEnabled ? 'AN' : 'AUS'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setQuickstartDisplayRankingToastEnabled((prev) => !prev)}
                          className={`inline-flex items-center gap-1.5 rounded border px-2.5 py-1 text-[11px] font-medium ${
                            quickstartDisplayRankingToastEnabled
                              ? 'border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100'
                              : 'border-gray-300 bg-gray-50 text-gray-600 hover:bg-gray-100'
                          }`}
                          title="Top-5-Ranking-Toast mit altem/neuem Punktestand (animiert)"
                        >
                          Top 5 Ranking: {quickstartDisplayRankingToastEnabled ? 'AN' : 'AUS'}
                        </button>
                      </div>

                      {/* Display Mirror Panel */}
                      {quickstartCenterView === 'display' ? (
                        <div className="flex-1 overflow-hidden">
                          <DisplayMirrorPanel displayPin={connectedDisplayPin} />
                        </div>
                      ) : (
                      <div
                        ref={quickstartStudentsGridRef}
                        className="flex min-h-0 flex-1 items-center justify-center overflow-hidden p-3"
                      >
                      {quickstartSelectedSitzplan ? (
                        <div
                          className="grid"
                          style={{
                            gap: QUICKSTART_STUDENT_TILE_GAP,
                            gridTemplateColumns: `repeat(${Math.max(1, quickstartCompactGrid.cols.length)}, ${quickstartStudentGridLayout.tileSize}px)`,
                            gridTemplateRows: `repeat(${Math.max(1, quickstartCompactGrid.rows.length)}, ${quickstartStudentGridLayout.tileSize}px)`
                          }}
                        >
                          {quickstartCompactGrid.cells.map(({ row, col }, index) => {
                            const pos = quickstartSelectedSitzplan.positionen.find(
                              (entry) => entry.grid_row === row && entry.grid_col === col
                            );
                            const studentId = pos?.sophomorix_unid || null;
                            const student = studentId ? quickstartStudentMap.get(String(studentId)) : null;

                            if (!student) {
                              return (
                                <div
                                  key={`quickstart-pos-${index}`}
                                  className="rounded-md border border-transparent bg-transparent"
                                  style={{
                                    width: quickstartStudentGridLayout.tileSize,
                                    height: quickstartStudentGridLayout.tileSize
                                  }}
                                />
                              );
                            }
                            return renderQuickstartStudentTile(student, `quickstart-pos-${index}`);
                          })}
                        </div>
                      ) : quickstartFallbackStudents.length > 0 ? (
                        <div
                          className="grid"
                          style={{
                            gap: QUICKSTART_STUDENT_TILE_GAP,
                            gridTemplateColumns: `repeat(${quickstartStudentGridLayout.cols}, ${quickstartStudentGridLayout.tileSize}px)`,
                            gridTemplateRows: `repeat(${quickstartStudentGridLayout.rows}, ${quickstartStudentGridLayout.tileSize}px)`
                          }}
                        >
                          {quickstartFallbackStudents.map((student) =>
                            renderQuickstartStudentTile(student, `quickstart-alpha-${student.id}`)
                          )}
                        </div>
                      ) : (
                        <div className="flex h-full items-center justify-center text-sm text-gray-500">
                          Keine Schüler für diesen Kontext gefunden.
                        </div>
                      )}
                      </div>
                      )}
                    </section>

                    {!isRightPanelCollapsed ? (
                      <aside
                        className="flex min-h-[320px] flex-col gap-2 self-start rounded-md border border-gray-200 bg-gray-50 p-3 lg:sticky lg:top-20"
                        style={{ height: quickstartWorkspaceHeight ?? undefined }}
                      >
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold text-gray-600">Extras</p>
                          <button
                            type="button"
                            onClick={() => setIsRightPanelCollapsed(true)}
                            className="rounded p-1 text-gray-500 hover:bg-gray-200"
                            title="Rechte Spalte einklappen"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowKeywordsModal(true)}
                          className="flex w-full items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2.5 text-left text-sm font-medium text-gray-700 shadow-sm hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 transition-colors"
                        >
                          <Lightbulb className="h-4 w-4 shrink-0 text-amber-500" />
                          <span>Stichworte</span>
                          {(quickstartContext.keywordHints || []).length > 0 && (
                            <span className="ml-auto text-[11px] text-gray-400">
                              {(quickstartContext.keywordHints || []).length}
                            </span>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowImagesModal(true)}
                          className="flex w-full items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2.5 text-left text-sm font-medium text-gray-700 shadow-sm hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-colors"
                        >
                          <Image className="h-4 w-4 shrink-0 text-blue-500" />
                          <span>Bildideen</span>
                          {((quickstartContext.imageGallery || []).reduce((s, g) => s + (g.images?.length || 0), 0) > 0 || manualImageGallery.length > 0) && (
                            <span className="ml-auto text-[11px] text-gray-400">
                              {manualImageGallery.length > 0
                                ? manualImageGallery.reduce((s, g) => s + (g.images?.length || 0), 0)
                                : (quickstartContext.imageGallery || []).reduce((s, g) => s + (g.images?.length || 0), 0)}
                            </span>
                          )}
                        </button>
                        <div className="rounded-md border border-gray-200 bg-white p-2">
                          <div className="mb-2 flex items-center justify-between">
                            <p className="text-xs font-semibold text-gray-700">Mediendepot</p>
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => plannedMediaFileInputRef.current?.click()}
                                disabled={plannedMediaUploading}
                                className="rounded border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-600 hover:bg-gray-100"
                                title="Datei hochladen (Bild/Video/PDF)"
                              >
                                {plannedMediaUploading ? '…' : 'Upload'}
                              </button>
                              <span className="text-[11px] text-gray-400">{plannedMediaItems.length}</span>
                            </div>
                          </div>
                          <div className="mb-2 grid grid-cols-[1fr_auto] gap-1.5">
                            <input
                              value={plannedMediaUrlInput}
                              onChange={(event) => setPlannedMediaUrlInput(event.target.value)}
                              className="rounded border border-gray-200 px-2 py-1 text-[11px] text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                              placeholder="YouTube- oder Medien-URL"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const normalizedUrl = plannedMediaUrlInput.trim();
                                if (!normalizedUrl) {
                                  toast.error('Bitte eine Medien-URL eingeben');
                                  return;
                                }
                                handleAddImageToPlannedMedia({
                                  mediaUrl: normalizedUrl,
                                  title: plannedMediaTitleInput.trim() || undefined,
                                  sourcePageUrl: normalizedUrl
                                });
                                setPlannedMediaUrlInput('');
                              }}
                              className="rounded border border-gray-200 px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-100"
                            >
                              URL+
                            </button>
                          </div>
                          {plannedMediaItems.length === 0 ? (
                            <p className="text-[11px] text-gray-500">
                              Lege Medien in den Bildideen mit <span className="font-semibold">+Depot</span> ab.
                            </p>
                          ) : (
                            <div className="max-h-52 space-y-1.5 overflow-y-auto pr-1">
                              {plannedMediaItems.map((item) => (
                                <div key={item.id} className="flex items-center gap-2 rounded border border-gray-200 px-2 py-1.5">
                                  {item.type === 'media' && item.mediaType === 'image' && item.mediaUrl ? (
                                    <img src={item.mediaUrl} alt={item.title || 'Medium'} className="h-8 w-8 rounded object-cover" />
                                  ) : item.type === 'media' ? (
                                    <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-100 text-[10px] font-medium text-gray-600">
                                      {item.mediaType === 'video' ? 'VID' : item.mediaType === 'youtube' ? 'YT' : 'PDF'}
                                    </div>
                                  ) : (
                                    <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-100 text-[10px] text-gray-500">
                                      TXT
                                    </div>
                                  )}
                                  <div className="min-w-0 flex-1">
                                    <p className="truncate text-[11px] font-medium text-gray-700">{item.title || 'Medium'}</p>
                                    {(item.originalName || item.uploadedAt || item.lastUsedAt) && (
                                      <p className="truncate text-[10px] text-gray-500">
                                        {item.originalName ? `Original: ${item.originalName}` : ''}
                                        {item.uploadedAt ? ` · Hochgeladen: ${new Date(item.uploadedAt).toLocaleDateString()}` : ''}
                                        {item.lastUsedAt ? ` · Zuletzt genutzt: ${new Date(item.lastUsedAt).toLocaleDateString()}` : ''}
                                      </p>
                                    )}
                                  </div>
                                  {connectedDisplayPin && (
                                    <button
                                      type="button"
                                      onClick={() => handlePresentPlannedMediaOnDisplay(item)}
                                      className="rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 hover:bg-blue-100"
                                      title="Ans Display senden"
                                    >
                                      Display
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => handleRemovePlannedMediaItem(item.id)}
                                    className="rounded border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-500 hover:bg-gray-100"
                                    title="Entfernen"
                                  >
                                    X
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {connectedDisplayPin && (
                          <div className="mt-2 rounded-md border border-gray-200 bg-white p-2">
                            <div className="mb-2 flex items-center justify-between">
                              <p className="text-xs font-semibold text-gray-700">Tafelbilder – Speichern & Laden</p>
                              <button
                                type="button"
                                onClick={() => {
                                  setShowSnapshotPanel((prev) => !prev);
                                  if (!showSnapshotPanel) void loadDisplaySnapshots();
                                }}
                                className="rounded border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-600 hover:bg-gray-100"
                              >
                                {showSnapshotPanel ? 'Ausblenden' : 'Anzeigen'}
                              </button>
                            </div>
                            {showSnapshotPanel && (
                              <div className="space-y-2">
                                <div className="flex gap-1.5">
                                  <input
                                    value={snapshotNameInput}
                                    onChange={(event) => setSnapshotNameInput(event.target.value)}
                                    className="flex-1 rounded border border-gray-200 px-2 py-1 text-[11px] text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                                    placeholder="Snapshot-Name (optional)"
                                  />
                                  <input
                                    value={snapshotFolderInput}
                                    onChange={(event) => setSnapshotFolderInput(event.target.value)}
                                    className="w-28 rounded border border-gray-200 px-2 py-1 text-[11px] text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                                    placeholder="Ordner"
                                  />
                                  <button
                                    type="button"
                                    onClick={handleSaveDisplaySnapshot}
                                    disabled={snapshotSaving}
                                    className="inline-flex items-center gap-1 rounded border border-green-200 bg-green-50 px-2 py-1 text-[10px] font-medium text-green-700 hover:bg-green-100 disabled:opacity-50"
                                    title="Aktuellen Display-Zustand speichern"
                                  >
                                    {snapshotSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                                    Speichern
                                  </button>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <input
                                    value={snapshotSearchInput}
                                    onChange={(event) => setSnapshotSearchInput(event.target.value)}
                                    className="flex-1 rounded border border-gray-200 px-2 py-1 text-[11px] text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                                    placeholder="Suchen (Name/Ordner/Thema)"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setSnapshotFavoritesOnly((prev) => !prev)}
                                    className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[10px] ${
                                      snapshotFavoritesOnly ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-600 hover:bg-gray-100'
                                    }`}
                                  >
                                    <Star className="h-3 w-3" />
                                    Favoriten
                                  </button>
                                </div>
                                {displaySnapshots.length === 0 ? (
                                  <p className="text-[11px] text-gray-500">Noch keine Snapshots gespeichert.</p>
                                ) : (
                                  <div className="max-h-40 space-y-1 overflow-y-auto pr-1">
                                    {displaySnapshots.map((snap) => (
                                      <div key={snap.id} className="flex items-center gap-2 rounded border border-gray-200 px-2 py-1.5">
                                        <div className="min-w-0 flex-1">
                                          <p className="truncate text-[11px] font-medium text-gray-700">{snap.name || 'Snapshot'}</p>
                                          <p className="text-[10px] text-gray-500">
                                            {snap.pageCount || 1} Seiten · {snap.itemCount} Objekte · {new Date(snap.createdAt).toLocaleString('de-DE')}
                                          </p>
                                          {snap.folderName ? <p className="text-[10px] text-gray-500">Ordner: {snap.folderName}</p> : null}
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => handleToggleSnapshotFavorite(snap.id, Boolean(snap.isFavorite))}
                                          className={`rounded border px-1.5 py-0.5 text-[10px] ${
                                            snap.isFavorite ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-500 hover:bg-gray-100'
                                          }`}
                                          title="Favorit markieren"
                                        >
                                          ★
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleRestoreDisplaySnapshot(snap.id)}
                                          disabled={snapshotRestoring === snap.id}
                                          className="rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                                          title="Snapshot auf Display laden"
                                        >
                                          {snapshotRestoring === snap.id ? '…' : 'Laden'}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleDeleteDisplaySnapshot(snap.id)}
                                          className="rounded border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-500 hover:bg-gray-100"
                                          title="Snapshot löschen"
                                        >
                                          X
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </aside>
                    ) : (
                      <aside className="flex min-h-[320px] self-start items-start justify-center rounded-md border border-gray-200 bg-gray-50 p-1 lg:sticky lg:top-20" style={{ height: quickstartWorkspaceHeight ?? undefined }}>
                        <button
                          type="button"
                          onClick={() => setIsRightPanelCollapsed(false)}
                          className="rounded p-1 text-gray-500 hover:bg-gray-200"
                          title="Rechte Spalte ausklappen"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                      </aside>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {unterrichtenMode === 'structure' && (
            <form onSubmit={handleCreate} className="space-y-4 px-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Titel</label>
              <input
                className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="z. B. Mathematik – Bruchrechnung, 5a"
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-gray-700">Kontext</label>
                <select
                  className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  value={parentType}
                  onChange={(event) => {
                    const nextType = event.target.value as ParentType;
                    setParentType(nextType);
                    setSelectedParentId('');
                    setSelectedClassName('');
                  }}
                >
                  <option value="class">Klasse</option>
                  <option value="course">Kurs</option>
                  <option value="group">Gruppe</option>
                  <option value="ad_hoc">Ad-hoc (individuell)</option>
                </select>
              </div>

              {parentType !== 'ad_hoc' && (
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    {parentType === 'class' ? 'Klasse' : parentType === 'course' ? 'Kurs' : 'Gruppe'}
                  </label>
                  <select
                    className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    value={selectedParentId}
                    onChange={(event) => {
                      const value = event.target.value;

                      if (parentType === 'class') {
                        setSelectedParentId(value);
                        const match = classOptions.find((cls: any) => cls.value === value);
                        setSelectedClassName(match?.label ?? '');
                      } else {
                        setSelectedParentId(value ? Number(value) : '');
                      }
                    }}
                  >
                    <option value="">auswählen…</option>
                    {parentType === 'class' &&
                      classOptions.map((option: any) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    {parentType === 'course' &&
                      courseOptions.map((option: any) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    {parentType === 'group' &&
                      groupOptions.map((option: any) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                  </select>
                  {parentType === 'class' && selectedClassName && classStudentCount !== undefined && (
                    <p className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                      <Users className="h-3 w-3" />
                      {classStudentCount} Schüler in {selectedClassName}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-gray-700">Fach (optional)</label>
                <select
                  className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  value={subjectId}
                  onChange={(event) => setSubjectId(event.target.value ? Number(event.target.value) : '')}
                >
                  <option value="">kein Fach</option>
                  {subjectsFromMeta.map((subject: any) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Curriculum (optional)</label>
                <select
                  className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  value={curriculumId}
                  onChange={(event) => setCurriculumId(event.target.value ? Number(event.target.value) : '')}
                >
                  <option value="">kein Curriculum</option>
                  {curriculaFromMeta.map((curriculum: any) => (
                    <option key={curriculum.id} value={curriculum.id}>
                      {curriculum.subject ? `${curriculum.subject} · ${curriculum.name}` : curriculum.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Sitzplan (optional)</label>
              <select
                className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                value={primarySitzplanId}
                onChange={(event) =>
                  setPrimarySitzplanId(event.target.value ? Number(event.target.value) : '')
                }
              >
                <option value="">kein Sitzplan</option>
                {selectableSitzplaene.map((plan: any) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-gray-700">Start (optional)</label>
                <div className="mt-1 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <input
                    type="datetime-local"
                    value={startAt}
                    onChange={(event) => setStartAt(event.target.value)}
                    className="flex-1 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Ende (optional)</label>
                <div className="mt-1 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <input
                    type="datetime-local"
                    value={endAt}
                    onChange={(event) => setEndAt(event.target.value)}
                    className="flex-1 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
              <label className="text-sm font-medium text-gray-700">Automationen</label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={nfcAutoEnabled}
                  onChange={(event) => setNfcAutoEnabled(event.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                NFC-Automatik aktivieren (Kompetenzbelege & Anwesenheit per Karte)
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={docAutoEnabled}
                  onChange={(event) => setDocAutoEnabled(event.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Dokumentationsautomatik vormerken
              </label>
            </div>

            <div className="space-y-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Mediendepot (für Display)</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => plannedMediaFileInputRef.current?.click()}
                    disabled={plannedMediaUploading}
                    className="rounded border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
                  >
                    {plannedMediaUploading ? 'Upload…' : 'Datei hochladen'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowImagesModal(true)}
                    className="rounded border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
                  >
                    Bildideen suchen
                  </button>
                </div>
              </div>
              <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                <input
                  value={plannedMediaTitleInput}
                  onChange={(event) => setPlannedMediaTitleInput(event.target.value)}
                  className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="Titel (optional)"
                />
                <input
                  value={plannedMediaUrlInput}
                  onChange={(event) => setPlannedMediaUrlInput(event.target.value)}
                  className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="Medien-URL (Bild/Video/PDF)"
                />
                <button
                  type="button"
                  onClick={() => {
                    const normalizedUrl = plannedMediaUrlInput.trim();
                    if (!normalizedUrl) {
                      toast.error('Bitte eine Medien-URL eingeben');
                      return;
                    }
                    handleAddImageToPlannedMedia({
                      mediaUrl: normalizedUrl,
                      title: plannedMediaTitleInput.trim() || undefined,
                      sourcePageUrl: normalizedUrl
                    });
                    setPlannedMediaUrlInput('');
                    setPlannedMediaTitleInput('');
                  }}
                  className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                >
                  URL hinterlegen
                </button>
              </div>
              <div className="grid gap-2">
                <textarea
                  value={plannedMediaTextInput}
                  onChange={(event) => setPlannedMediaTextInput(event.target.value)}
                  rows={3}
                  className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="Optional: Textkarte für das Display"
                />
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      if (!plannedMediaTextInput.trim()) {
                        toast.error('Bitte zuerst einen Text eingeben');
                        return;
                      }
                      handleAddTextToPlannedMedia({
                        title: plannedMediaTitleInput.trim() || 'Textkarte',
                        text: plannedMediaTextInput
                      });
                      setPlannedMediaTextInput('');
                      setPlannedMediaTitleInput('');
                    }}
                    className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                  >
                    Text hinterlegen
                  </button>
                </div>
              </div>
              {plannedMediaItems.length === 0 ? (
                <p className="text-xs text-gray-500">
                  Lege hier Medien für diese Unterrichtseinheit ab. Sie stehen später im Unterricht für das Display bereit.
                </p>
              ) : (
                <div className="max-h-52 space-y-1.5 overflow-y-auto pr-1">
                  {plannedMediaItems.map((item) => (
                    <div key={item.id} className="flex items-center gap-2 rounded border border-gray-200 bg-white px-2 py-1.5">
                      {item.type === 'media' && item.mediaType === 'image' && item.mediaUrl ? (
                        <img src={item.mediaUrl} alt={item.title || 'Medium'} className="h-8 w-8 rounded object-cover" />
                      ) : item.type === 'media' ? (
                        <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-100 text-[10px] font-medium text-gray-600">
                          {item.mediaType === 'video' ? 'VID' : item.mediaType === 'youtube' ? 'YT' : 'PDF'}
                        </div>
                      ) : (
                        <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-100 text-[10px] text-gray-500">
                          TXT
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[11px] font-medium text-gray-700">{item.title || 'Medium'}</p>
                        {(item.originalName || item.uploadedAt || item.lastUsedAt) && (
                          <p className="truncate text-[10px] text-gray-500">
                            {item.originalName ? `Original: ${item.originalName}` : ''}
                            {item.uploadedAt ? ` · Hochgeladen: ${new Date(item.uploadedAt).toLocaleDateString()}` : ''}
                            {item.lastUsedAt ? ` · Zuletzt genutzt: ${new Date(item.lastUsedAt).toLocaleDateString()}` : ''}
                          </p>
                        )}
                      </div>
                      {connectedDisplayPin && (
                        <button
                          type="button"
                          onClick={() => handlePresentPlannedMediaOnDisplay(item)}
                          className="rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 hover:bg-blue-100"
                          title="Ans Display senden"
                        >
                          Display
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemovePlannedMediaItem(item.id)}
                        className="rounded border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-500 hover:bg-gray-100"
                        title="Entfernen"
                      >
                        X
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {connectedDisplayPin && (
              <div className="space-y-2 rounded-md border border-slate-700 bg-slate-950 px-3 py-2">
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-1.5 text-sm font-medium text-slate-200">
                    <Monitor className="h-4 w-4 text-slate-400" />
                    Tafel-Ansicht
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-sky-400" />
                  </label>
                  <span className="font-mono text-[11px] text-slate-500">{connectedDisplayPin}</span>
                </div>
                <div className="h-72 overflow-hidden rounded-md">
                  <DisplayMirrorPanel displayPin={connectedDisplayPin} />
                </div>
              </div>
            )}

            {connectedDisplayPin && (
              <div className="space-y-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Tafelbilder – Speichern & Laden</label>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSnapshotPanel((prev) => !prev);
                      if (!showSnapshotPanel) void loadDisplaySnapshots();
                    }}
                    className="rounded border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
                  >
                    <FolderOpen className="mr-1 inline h-3 w-3" />
                    {showSnapshotPanel ? 'Ausblenden' : 'Anzeigen'}
                  </button>
                </div>
                {showSnapshotPanel && (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        value={snapshotNameInput}
                        onChange={(event) => setSnapshotNameInput(event.target.value)}
                        className="flex-1 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        placeholder="Snapshot-Name (optional)"
                      />
                      <input
                        value={snapshotFolderInput}
                        onChange={(event) => setSnapshotFolderInput(event.target.value)}
                        className="w-36 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        placeholder="Ordner"
                      />
                      <button
                        type="button"
                        onClick={handleSaveDisplaySnapshot}
                        disabled={snapshotSaving}
                        className="inline-flex items-center gap-1 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-100 disabled:opacity-50"
                        title="Aktuellen Display-Zustand speichern"
                      >
                        {snapshotSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Speichern
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        value={snapshotSearchInput}
                        onChange={(event) => setSnapshotSearchInput(event.target.value)}
                        className="flex-1 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        placeholder="Suchen (Name/Ordner/Thema)"
                      />
                      <button
                        type="button"
                        onClick={() => setSnapshotFavoritesOnly((prev) => !prev)}
                        className={`inline-flex items-center gap-1 rounded-md border px-3 py-2 text-sm ${
                          snapshotFavoritesOnly ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Star className="h-4 w-4" />
                        Favoriten
                      </button>
                    </div>
                    {displaySnapshots.length === 0 ? (
                      <p className="text-xs text-gray-500">Noch keine Snapshots gespeichert.</p>
                    ) : (
                      <div className="max-h-52 space-y-1.5 overflow-y-auto pr-1">
                        {displaySnapshots.map((snap) => (
                          <div key={snap.id} className="flex items-center gap-2 rounded border border-gray-200 bg-white px-2 py-1.5">
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-[11px] font-medium text-gray-700">{snap.name || 'Snapshot'}</p>
                              <p className="text-[10px] text-gray-500">
                                {snap.pageCount || 1} Seiten · {snap.itemCount} Objekte · {new Date(snap.createdAt).toLocaleString('de-DE')}
                              </p>
                              {snap.folderName ? <p className="text-[10px] text-gray-500">Ordner: {snap.folderName}</p> : null}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleToggleSnapshotFavorite(snap.id, Boolean(snap.isFavorite))}
                              className={`rounded border px-1.5 py-0.5 text-[10px] ${
                                snap.isFavorite ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-500 hover:bg-gray-100'
                              }`}
                              title="Favorit markieren"
                            >
                              ★
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRestoreDisplaySnapshot(snap.id)}
                              disabled={snapshotRestoring === snap.id}
                              className="rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                              title="Snapshot auf Display laden"
                            >
                              {snapshotRestoring === snap.id ? '…' : 'Laden'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteDisplaySnapshot(snap.id)}
                              className="rounded border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-500 hover:bg-gray-100"
                              title="Snapshot löschen"
                            >
                              X
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {parentType !== 'ad_hoc' && (
              <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Teilnehmer-Vorschau</label>
                  {classStudentCount !== undefined && (
                    <span className="text-xs text-gray-500">{classStudentCount} Schüler</span>
                  )}
                </div>
                <div className="mt-2 space-y-2">
                  {contextLoading && (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Loader2 className="h-4 w-4 animate-spin text-primary-500" />
                      Lade Teilnehmer …
                    </div>
                  )}
                  {contextError && (
                    <div className="text-xs text-red-500">{contextError}</div>
                  )}
                  {!contextLoading && !contextError && contextStudents.length === 0 && (
                    <p className="text-xs text-gray-500">
                      Noch keine Teilnehmer geladen. Bitte wählen Sie eine gültige Klasse, einen Kurs oder eine Gruppe.
                    </p>
                  )}
                  {!contextLoading &&
                    contextError === null &&
                    contextStudents.slice(0, 6).map((student) => (
                      <div
                        key={student.id}
                        className="flex items-center justify-between rounded border border-gray-100 bg-white px-3 py-2 text-sm text-gray-700"
                      >
                        <span>
                          {student.firstName} {student.lastName}
                        </span>
                        <span className="text-xs text-gray-400">
                          {student.className || '–'}
                        </span>
                      </div>
                    ))}
                  {contextStudents.length > 6 && (
                    <p className="text-xs text-gray-500">
                      … und {contextStudents.length - 6} weitere Schüler
                    </p>
                  )}
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={createMutation.isLoading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-primary-300"
            >
              {createMutation.isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Speichere …
                </>
              ) : (
                <>
                  <PlusCircle className="h-4 w-4" />
                  Unterrichtseinheit erstellen
                </>
              )}
            </button>

            <div className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-500">
              <p className="flex items-center gap-1">
                <Info className="h-3 w-3" />
                Teilnehmerlisten werden beim Start automatisch aus der gewählten Klasse, dem Kurs oder der Gruppe
                ermittelt.
              </p>
              <p className="mt-1 flex items-center gap-1">
                <Target className="h-3 w-3" />
                Kompetenzen und Inhalte lassen sich nach dem Erstellen in der Unterrichtsansicht flexibel ergänzen.
              </p>
            </div>
            </form>
          )}
        </section>
      </div>

      {unterrichtenMode === 'structure' && (
        <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-4 py-3">
            <h2 className="text-lg font-medium text-gray-900">Details & Zusammenfassung</h2>
            <p className="mt-1 text-sm text-gray-500">
              Auswahl aus der Liste zeigt Teilnehmer, Kompetenzen und Ideen. Statuswechsel erfolgt über das Kontextmenü.
            </p>
          </div>

          <div className="px-4 py-4">
            {!selectedUnitId && (
              <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
                Wählen Sie links eine Unterrichtseinheit aus, um Details zu sehen.
              </div>
            )}

            {selectedUnitId && detailLoading && (
              <div className="flex items-center justify-center rounded-md border border-gray-200 bg-gray-50 px-4 py-6">
                <Loader2 className="h-5 w-5 animate-spin text-primary-500" />
              </div>
            )}

            {selectedUnitId && !detailLoading && selectedUnit && selectedUnit.unit && (
              <div className="grid gap-4 lg:grid-cols-3">
                <div className="lg:col-span-1 space-y-3 rounded-md border border-gray-200 bg-white px-4 py-3 shadow-sm">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">{selectedUnit.unit.title}</h3>
                    <p className="text-xs text-gray-500">
                      Erstellt am {new Date(selectedUnit.unit.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center rounded-full bg-primary-50 px-2 py-1 text-xs font-medium text-primary-600">
                      Status: {selectedUnit.unit.status}
                    </span>
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                      Kontext: {selectedUnit.unit.parent_type}
                    </span>
                    {selectedUnit.unit.curriculum_id && (
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                        Curriculum #{selectedUnit.unit.curriculum_id}
                      </span>
                    )}
                  </div>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span>
                        {selectedUnit.unit.start_at
                          ? format(parseISO(selectedUnit.unit.start_at), 'dd.MM.yyyy HH:mm')
                          : 'Start offen'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-gray-400" />
                      <span>{selectedUnit.unit.participant_count} aktive Teilnehmer</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-gray-400" />
                      <span>{selectedUnit.unit.competency_count} Kompetenzen geplant</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-gray-400" />
                      <span>NFC-Automatik: {selectedUnit.unit.nfc_auto_enabled ? 'aktiv' : 'inaktiv'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-gray-400" />
                      <span>Dokumentationsautomatik: {selectedUnit.unit.doc_auto_enabled ? 'aktiv' : 'inaktiv'}</span>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-1 rounded-md border border-gray-200 bg-white px-4 py-3 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-900">Teilnehmer</h3>
                  <div className="mt-2 space-y-2">
                    {selectedUnit.participants.length === 0 && (
                      <p className="text-sm text-gray-500">
                        Noch keine Teilnehmer gespeichert. Diese werden beim Unterrichtsbeginn automatisch übernommen.
                      </p>
                    )}
                    {selectedUnit.participants.slice(0, 12).map((participant) => {
                      const displayName =
                        [participant.first_name, participant.last_name].filter(Boolean).join(' ') ||
                        participant.student_id;
                      const identifier = participant.sophomorix_unid || participant.student_id;
                      return (
                        <div
                          key={`${participant.teaching_unit_id}-${participant.student_id}`}
                          className="rounded border border-gray-100 px-3 py-2 text-sm text-gray-700"
                        >
                          <div className="font-medium text-gray-800">{displayName}</div>
                          <div className="text-xs text-gray-500">
                            {participant.klasse ? `${participant.klasse} · ` : ''}
                            {identifier}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">Ursprung: {participant.origin}</div>
                        </div>
                      );
                    })}
                    {selectedUnit.participants.length > 12 && (
                      <p className="text-xs text-gray-500">
                        … und {selectedUnit.participants.length - 12} weitere
                      </p>
                    )}
                  </div>
                </div>

                <div className="lg:col-span-1 rounded-md border border-gray-200 bg-white px-4 py-3 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-900">Kompetenzen & Ideen</h3>
                  <div className="mt-2 space-y-2">
                    {selectedUnit.competencies.length === 0 && (
                      <p className="text-sm text-gray-500">Noch keine Kompetenzen hinterlegt.</p>
                    )}
                    {selectedUnit.competencies.slice(0, 5).map((competency) => (
                      <div key={competency.id} className="rounded border border-gray-100 px-3 py-2">
                        <div className="text-sm font-medium text-gray-800">
                          {competency.competency_name || `Kompetenz #${competency.competency_id}`}
                        </div>
                        <div className="text-xs text-gray-500">
                          Zielstufe: {competency.target_level_id ?? '–'} | Fokus:{' '}
                          {competency.is_focus ? 'Ja' : 'Nein'}
                        </div>
                        {competency.notes && <p className="mt-1 text-xs text-gray-500">{competency.notes}</p>}
                      </div>
                    ))}
                    {selectedUnit.ideas.length > 0 && (
                      <div className="rounded-md border border-dashed border-primary-200 bg-primary-50 px-3 py-2 text-xs text-primary-700">
                        {selectedUnit.ideas.length} LLM-Ideen gespeichert. Detailabruf folgt im nächsten Schritt.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {selectedStudentForEmail && (
        <EmailModal
          isOpen={showEmailModal}
          onClose={() => {
            setShowEmailModal(false);
            setSelectedStudentForEmail(null);
          }}
          to={undefined}
          studentName={`${selectedStudentForEmail.firstName} ${selectedStudentForEmail.lastName} (${selectedStudentForEmail.className})`}
          studentUnid={selectedStudentForEmail.id}
          context="sitzordnung"
          initialSubject={`Nachricht zu ${selectedStudentForEmail.firstName} ${selectedStudentForEmail.lastName}`}
          initialBody={`Sehr geehrte Eltern,\n\nbezüglich Ihres Kindes ${selectedStudentForEmail.firstName} ${selectedStudentForEmail.lastName} (${selectedStudentForEmail.className}).\n\nMit freundlichen Grüßen`}
        />
      )}

      <input
        ref={plannedMediaFileInputRef}
        type="file"
        accept="image/*,video/*,application/pdf"
        className="hidden"
        onChange={async (event) => {
          const file = event.target.files?.[0];
          await handleUploadPlannedMediaFile(file);
          event.currentTarget.value = '';
        }}
      />

      {metaLoading && (
        <div className="rounded-md border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600 shadow-sm">
          Metadaten werden geladen …
        </div>
      )}
    </div>
  );
}
